:root {
      --acc-navy: #002446;
      --acc-navy-strong: #001b35;
      --acc-navy-soft: #e9f1f8;
      --acc-gold: #9a7425;
      --acc-gold-bright: #f5d78b;
      --acc-gold-soft: #fff7df;
      --surface: #ffffff;
      --surface-sunken: #f7f8fa;
      --surface-raised: #ffffff;
      --surface-hover: #f1f3f5;
      --text: #172b4d;
      --text-subtle: #44546f;
      --text-subtlest: #626f86;
      --border: #dcdfe4;
      --border-bold: #b7bdc8;
      --success-bg: #dcfff1;
      --success-text: #216e4e;
      --info-bg: #e9f2ff;
      --info-text: #0c66e4;
      --warning-bg: #fff7d6;
      --warning-text: #7f5f01;
      --danger-bg: #ffeceb;
      --danger-text: #ae2a19;
      --shadow-raised: 0 1px 1px rgba(9, 30, 66, .10), 0 0 1px rgba(9, 30, 66, .18);
      --shadow-overlay: 0 8px 24px rgba(9, 30, 66, .18), 0 0 1px rgba(9, 30, 66, .20);
      --radius-sm: 6px;
      --radius-md: 8px;
      --radius-lg: 12px;
      --radius-xl: 20px;
      --max: 1200px;
    }

    * { box-sizing: border-box; }
    html { scroll-behavior: smooth; }
    body {
      margin: 0;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      color: var(--text);
      background: var(--surface);
      line-height: 1.5;
      -webkit-font-smoothing: antialiased;
    }
    button, input, select, textarea { font: inherit; }
    button, a { -webkit-tap-highlight-color: transparent; }
    a { color: inherit; text-decoration: none; }
    img { display: block; max-width: 100%; }
    :focus-visible { outline: 3px solid rgba(12, 102, 228, .35); outline-offset: 2px; }

    .skip-link { position: fixed; left: 16px; top: -60px; z-index: 999; padding: 10px 14px; background: white; border-radius: 8px; box-shadow: var(--shadow-overlay); }
    .skip-link:focus { top: 16px; }
    .shell { min-height: 100vh; }
    .container { width: min(calc(100% - 40px), var(--max)); margin-inline: auto; }
    .section { padding: 88px 0; }
    .section--soft { background: var(--surface-sunken); }
    .eyebrow { margin: 0 0 10px; color: var(--acc-gold); font-size: .76rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
    h1, h2, h3, p { margin-top: 0; }
    h1 { font-size: clamp(2.55rem, 6vw, 5.35rem); line-height: .98; letter-spacing: -.055em; }
    h2 { font-size: clamp(2rem, 4vw, 3.25rem); line-height: 1.08; letter-spacing: -.04em; margin-bottom: 16px; }
    h3 { font-size: 1.08rem; line-height: 1.3; letter-spacing: -.018em; }
    .lead { max-width: 680px; color: var(--text-subtle); font-size: 1.08rem; }
    .section-head { display: flex; align-items: end; justify-content: space-between; gap: 28px; margin-bottom: 32px; }
    .section-head > div:first-child { max-width: 760px; }

    .btn {
      min-height: 40px;
      border: 0;
      border-radius: var(--radius-md);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 0 16px;
      background: var(--surface-hover);
      color: var(--text);
      font-weight: 700;
      cursor: pointer;
      transition: background .16s ease, color .16s ease, transform .16s ease, box-shadow .16s ease;
    }
    .btn:hover { background: #e5e7eb; }
    .btn:active { transform: translateY(1px); }
    .btn--primary { background: var(--acc-navy); color: white; }
    .btn--primary:hover { background: #073a69; }
    .btn--gold { background: var(--acc-gold-bright); color: var(--acc-navy-strong); }
    .btn--gold:hover { background: #ffe6a8; }
    .btn--subtle { background: transparent; color: var(--text-subtle); }
    .btn--subtle:hover { background: var(--surface-hover); color: var(--text); }
    .btn--white { background: white; color: var(--acc-navy); box-shadow: var(--shadow-raised); }
    .btn--white:hover { background: #f7f8fa; }
    .btn--block { width: 100%; }
    .btn--icon { width: 40px; padding: 0; }
    .btn svg { width: 18px; height: 18px; }

    .lozenge { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 3px 9px; font-size: .72rem; line-height: 1.35; font-weight: 800; text-transform: uppercase; letter-spacing: .035em; }
    .lozenge--success { background: var(--success-bg); color: var(--success-text); }
    .lozenge--info { background: var(--info-bg); color: var(--info-text); }
    .lozenge--warning { background: var(--warning-bg); color: var(--warning-text); }
    .lozenge--neutral { background: #f1f2f4; color: var(--text-subtle); }

    /* Public top navigation */
    .topbar {
      position: sticky;
      top: 0;
      z-index: 80;
      height: 72px;
      background: rgba(255,255,255,.92);
      border-bottom: 1px solid rgba(220,223,228,.9);
      backdrop-filter: blur(18px);
    }
    .topbar__inner { height: 100%; display: flex; align-items: center; gap: 24px; }
    .brand { display: inline-flex; align-items: center; gap: 10px; min-width: 260px; }
    .brand img { width: 44px; height: 44px; object-fit: contain; object-position: center; transition: opacity .16s ease; }
    img[data-media-state="loading"], img[data-media-state="empty"] { pointer-events: none; }
    .brand__copy strong { display: block; color: var(--acc-navy); font-size: .86rem; line-height: 1.12; }
    .brand__copy span { display: block; color: var(--text-subtlest); font-size: .66rem; margin-top: 3px; }
    .nav { margin-left: auto; display: flex; align-items: center; gap: 2px; }
    .nav a { padding: 8px 12px; border-radius: var(--radius-md); color: var(--text-subtle); font-size: .9rem; font-weight: 700; }
    .nav a:hover { background: var(--surface-hover); color: var(--text); }
    .topbar__actions { display: flex; align-items: center; gap: 8px; }
    .mobile-menu { display: none; }

    .prototype-banner { background: var(--info-bg); color: var(--info-text); border-bottom: 1px solid #b8d7ff; font-size: .78rem; font-weight: 650; }
    .prototype-banner .container { min-height: 34px; display: flex; align-items: center; justify-content: center; gap: 8px; }
    .prototype-banner.is-connected { background: var(--success-bg); color: var(--success-text); border-bottom-color: #7ee2b8; }
    .prototype-banner.is-error { background: var(--warning-bg); color: var(--warning-text); border-bottom-color: #f5cd47; }
    .prototype-banner svg { width: 15px; height: 15px; flex: 0 0 auto; }

    .mobile-navigation { position: absolute; top: 100%; left: 0; right: 0; z-index: 79; background: white; border-bottom: 1px solid var(--border); box-shadow: var(--shadow-overlay); }
    .mobile-navigation[hidden] { display: none; }
    .mobile-navigation .container { display: grid; padding-top: 10px; padding-bottom: 14px; }
    .mobile-navigation a, .mobile-navigation button { min-height: 44px; display: flex; align-items: center; gap: 10px; padding: 0 12px; border: 0; border-radius: var(--radius-md); background: transparent; color: var(--text-subtle); text-align: left; font-weight: 700; cursor: pointer; }
    .mobile-navigation a:hover, .mobile-navigation button:hover { background: var(--surface-hover); color: var(--text); }
    .mobile-navigation svg { width: 18px; height: 18px; }

    /* Hero */
    .hero { position: relative; min-height: 720px; display: grid; align-items: end; overflow: hidden; background: var(--acc-navy); color: white; }
    .hero__image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .hero::before { content: ""; position: absolute; inset: 0; z-index: 1; background: linear-gradient(90deg, rgba(0,22,43,.93) 0%, rgba(0,25,49,.68) 45%, rgba(0,20,39,.16) 78%), linear-gradient(0deg, rgba(0,24,47,.82), transparent 54%); }
    .hero__content { position: relative; z-index: 2; padding: 100px 0 64px; display: grid; grid-template-columns: 1fr 390px; gap: 72px; align-items: end; }
    .hero__copy { max-width: 740px; }
    .hero__brand { width: 76px; height: 76px; object-fit: contain; margin-bottom: 22px; opacity: .96; filter: drop-shadow(0 8px 20px rgba(0,0,0,.18)); }
    .hero__copy h1 { max-width: 760px; margin-bottom: 24px; }
    .hero__copy p { max-width: 620px; color: rgba(255,255,255,.82); font-size: 1.12rem; }
    .hero__actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 30px; }
    .search-panel { border: 1px solid rgba(255,255,255,.28); border-radius: var(--radius-xl); background: rgba(255,255,255,.96); color: var(--text); padding: 24px; box-shadow: var(--shadow-overlay); }
    .search-panel h3 { margin-bottom: 4px; }
    .search-panel > p { color: var(--text-subtle); font-size: .86rem; margin-bottom: 20px; }
    .field { display: grid; gap: 6px; margin-bottom: 14px; }
    .field label { color: var(--text-subtle); font-size: .78rem; font-weight: 800; }
    .input, .select {
      width: 100%;
      min-height: 42px;
      border: 1px solid var(--border-bold);
      border-radius: var(--radius-md);
      background: white;
      color: var(--text);
      padding: 0 12px;
      transition: border-color .16s ease, box-shadow .16s ease;
    }
    .input:hover, .select:hover { border-color: #8590a2; }
    .input:focus, .select:focus { border-color: #0c66e4; outline: 0; box-shadow: 0 0 0 3px rgba(12,102,228,.16); }
    .select-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .hero__meta { margin-top: 44px; display: flex; gap: 26px; flex-wrap: wrap; }
    .meta-item { display: flex; align-items: center; gap: 9px; color: rgba(255,255,255,.84); font-size: .83rem; font-weight: 650; }
    .meta-item svg { width: 18px; color: var(--acc-gold-bright); }

    /* Projects */
    .project-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
    .project-card { overflow: hidden; border: 1px solid var(--border); border-radius: var(--radius-lg); background: var(--surface-raised); box-shadow: var(--shadow-raised); transition: background .16s ease, box-shadow .16s ease, transform .16s ease; }
    .project-card:hover { background: var(--surface-hover); box-shadow: var(--shadow-overlay); transform: translateY(-2px); }
    .project-card__media { position: relative; aspect-ratio: 16/9; overflow: hidden; background: #dfe4ea; }
    .project-card__media img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s ease; }
    .project-card:hover img { transform: scale(1.025); }
    .project-card__badge { position: absolute; left: 16px; top: 16px; }
    .project-card__body { padding: 20px; }
    .project-card__top { display: flex; align-items: start; justify-content: space-between; gap: 20px; }
    .project-card h3 { font-size: 1.25rem; margin: 0 0 6px; }
    .project-card p { color: var(--text-subtle); font-size: .9rem; margin-bottom: 18px; }
    .project-card__foot { border-top: 1px solid var(--border); padding-top: 16px; display: flex; align-items: center; justify-content: space-between; gap: 14px; }
    .mini-meta { display: inline-flex; align-items: center; gap: 6px; color: var(--text-subtlest); font-size: .8rem; font-weight: 650; }
    .text-link { border: 0; background: transparent; color: var(--info-text); font-weight: 800; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; padding: 4px; border-radius: 5px; }
    .text-link:hover { background: var(--info-bg); }

    /* Process */
    .process-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
    .process-card { position: relative; padding: 22px; border: 1px solid var(--border); border-radius: var(--radius-lg); background: white; }
    .process-card:not(:last-child)::after { content: ""; position: absolute; right: -17px; top: 42px; width: 17px; height: 1px; background: var(--border-bold); }
    .step-icon { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 10px; background: var(--acc-navy-soft); color: var(--acc-navy); margin-bottom: 18px; }
    .step-icon svg { width: 20px; }
    .process-card h3 { margin-bottom: 7px; }
    .process-card p { color: var(--text-subtle); font-size: .84rem; margin-bottom: 0; }
    .step-number { position: absolute; right: 16px; top: 15px; color: #8993a4; font-size: .72rem; font-weight: 800; }

    /* Services and CTA */
    .service-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
    .service-card { padding: 24px; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--border); }
    .service-card .step-icon { background: var(--acc-gold-soft); color: var(--acc-gold); }
    .service-card p { color: var(--text-subtle); font-size: .88rem; }
    .cta-card { padding: clamp(32px, 6vw, 64px); border-radius: var(--radius-xl); background: var(--acc-navy); color: white; display: flex; align-items: center; justify-content: space-between; gap: 36px; overflow: hidden; position: relative; }
    .cta-card::after { content: ""; position: absolute; right: -110px; top: -170px; width: 420px; height: 420px; border-radius: 50%; border: 70px solid rgba(245,215,139,.08); }
    .cta-card > * { position: relative; z-index: 1; }
    .cta-card h2 { max-width: 650px; margin-bottom: 12px; }
    .cta-card p { max-width: 650px; color: rgba(255,255,255,.76); margin-bottom: 0; }
    .cta-card__actions { display: flex; gap: 10px; flex-shrink: 0; }

    .footer { background: #00182f; color: white; padding: 52px 0 24px; }
    .footer-grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr; gap: 60px; }
    .footer-brand { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
    .footer-brand img { width: 60px; height: 60px; object-fit: contain; margin: 0; }
    .footer-brand strong { display: block; font-size: .96rem; }
    .footer-brand span { display: block; color: rgba(255,255,255,.58); font-size: .72rem; margin-top: 3px; }
    .footer p, .footer a { color: rgba(255,255,255,.68); font-size: .84rem; }
    .footer h3 { font-size: .84rem; text-transform: uppercase; letter-spacing: .08em; color: var(--acc-gold-bright); }
    .footer-links { display: grid; gap: 10px; }
    .social-links { display: flex; align-items: center; gap: 14px; margin-top: 18px; }
    .social-links[hidden], .social-link[hidden], img[hidden] { display: none !important; }
    .social-link { width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; padding: 0; border: 0; background: transparent; color: rgba(255,255,255,.72) !important; line-height: 1; transition: color .16s ease, transform .16s ease, opacity .16s ease; }
    .social-link:hover { color: white !important; transform: translateY(-1px); }
    .social-link:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; border-radius: 4px; }
    .social-link svg { width: 19px; height: 19px; fill: currentColor; stroke: none; }
    .footer-bottom { border-top: 1px solid rgba(255,255,255,.12); margin-top: 38px; padding-top: 22px; display: flex; justify-content: space-between; gap: 20px; color: rgba(255,255,255,.52); font-size: .75rem; }

    /* CMS */
    #cmsShell { display: none; min-height: 100vh; background: var(--surface-sunken); }
    body.cms-mode #publicShell { display: none; }
    body.cms-mode #cmsShell { display: grid; grid-template-columns: 260px 1fr; }
    .cms-sidebar { position: sticky; top: 0; height: 100vh; background: var(--acc-navy-strong); color: white; padding: 18px 14px; display: flex; flex-direction: column; }
    .cms-brand { display: flex; align-items: center; gap: 10px; padding: 5px 8px 20px; border-bottom: 1px solid rgba(255,255,255,.12); }
    .cms-brand__mark { width: 40px; height: 40px; object-fit: contain; flex: 0 0 auto; }
    .cms-brand strong { display: block; font-size: .86rem; }
    .cms-brand span { display: block; color: rgba(255,255,255,.58); font-size: .7rem; }
    .cms-nav { display: grid; gap: 4px; margin-top: 18px; }
    .cms-nav button { width: 100%; min-height: 40px; border: 0; border-radius: var(--radius-md); background: transparent; color: rgba(255,255,255,.72); display: flex; align-items: center; gap: 11px; padding: 0 11px; text-align: left; cursor: pointer; font-weight: 650; font-size: .84rem; }
    .cms-nav button:hover { background: rgba(255,255,255,.08); color: white; }
    .cms-nav button.active { background: rgba(255,255,255,.14); color: white; }
    .cms-nav svg { width: 18px; }
    .cms-nav__label { padding: 18px 10px 6px; color: rgba(255,255,255,.38); font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .08em; }
    .cms-sidebar__bottom { margin-top: auto; }
    .cms-user { margin-top: 14px; padding: 12px 9px; border-top: 1px solid rgba(255,255,255,.12); display: flex; align-items: center; gap: 10px; }
    .avatar { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 50%; background: var(--acc-gold-bright); color: var(--acc-navy); font-size: .75rem; font-weight: 900; }
    .cms-user strong { display: block; font-size: .76rem; }
    .cms-user span { display: block; color: rgba(255,255,255,.56); font-size: .66rem; }
    .cms-main { min-width: 0; }
    .cms-topbar { height: 64px; border-bottom: 1px solid var(--border); background: rgba(255,255,255,.95); display: flex; align-items: center; padding: 0 28px; gap: 14px; position: sticky; top: 0; z-index: 30; backdrop-filter: blur(14px); }
    .cms-topbar h1 { margin: 0; font-size: 1rem; letter-spacing: -.015em; }
    .cms-topbar__actions { margin-left: auto; display: flex; gap: 8px; }
    .cms-content { padding: 30px; max-width: 1500px; margin-inline: auto; }
    .cms-data-status { margin: -8px 0 20px; padding: 12px 14px; border: 1px solid var(--border); border-radius: var(--radius-md); font-size: .78rem; font-weight: 650; }
    .cms-data-status--info { color: var(--acc-navy); background: var(--acc-navy-soft); border-color: rgba(18,58,99,.18); }
    .cms-data-status--success { color: #216e4e; background: #dcfff1; border-color: #7ee2b8; }
    .cms-data-status--warning { color: #974f0c; background: #fff7d6; border-color: #f5cd47; }
    .cms-empty-cell { padding: 28px !important; text-align: center; color: var(--text-subtle); }
    .cms-heading { display: flex; align-items: end; justify-content: space-between; gap: 22px; margin-bottom: 24px; }
    .cms-heading h2 { font-size: 1.65rem; margin-bottom: 5px; }
    .cms-heading p { color: var(--text-subtle); font-size: .86rem; margin-bottom: 0; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .kpi-card { padding: 18px; border: 1px solid var(--border); border-radius: var(--radius-lg); background: white; box-shadow: var(--shadow-raised); }
    .kpi-card__head { display: flex; align-items: center; justify-content: space-between; color: var(--text-subtle); font-size: .75rem; font-weight: 750; }
    .kpi-icon { width: 32px; height: 32px; display: grid; place-items: center; border-radius: 8px; background: var(--acc-navy-soft); color: var(--acc-navy); }
    .kpi-icon svg { width: 17px; }
    .kpi-value { font-size: 1.85rem; line-height: 1; font-weight: 800; margin: 18px 0 7px; letter-spacing: -.04em; }
    .kpi-foot { color: var(--text-subtlest); font-size: .72rem; }
    .cms-grid { display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(300px, .85fr); gap: 18px; }
    .panel { border: 1px solid var(--border); border-radius: var(--radius-lg); background: white; box-shadow: var(--shadow-raised); overflow: hidden; }
    .panel__head { min-height: 58px; padding: 13px 17px; display: flex; align-items: center; justify-content: space-between; gap: 14px; border-bottom: 1px solid var(--border); }
    .panel__head h3 { margin: 0; font-size: .92rem; }
    .panel__head p { margin: 2px 0 0; color: var(--text-subtlest); font-size: .7rem; }
    .table-wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: .8rem; }
    th { background: #fafbfc; color: var(--text-subtle); font-size: .7rem; text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--border); }
    td { padding: 12px 14px; border-bottom: 1px solid var(--border); vertical-align: middle; }
    tr:last-child td { border-bottom: 0; }
    .project-cell { display: flex; align-items: center; gap: 10px; min-width: 230px; }
    .project-cell img { width: 52px; height: 38px; object-fit: cover; border-radius: 6px; }
    .project-cell strong { display: block; font-size: .78rem; }
    .project-cell span { color: var(--text-subtlest); font-size: .68rem; }
    .activity-list { padding: 5px 16px 16px; }
    .activity { display: grid; grid-template-columns: 34px 1fr; gap: 10px; padding: 13px 0; border-bottom: 1px solid var(--border); }
    .activity:last-child { border-bottom: 0; }
    .activity__icon { width: 32px; height: 32px; display: grid; place-items: center; background: var(--surface-hover); border-radius: 8px; color: var(--text-subtle); }
    .activity__icon svg { width: 16px; }
    .activity strong { display: block; font-size: .76rem; }
    .activity p { color: var(--text-subtlest); font-size: .7rem; margin: 3px 0 0; }
    .toke-card { margin-top: 18px; padding: 18px; border: 1px dashed var(--border-bold); border-radius: var(--radius-lg); background: linear-gradient(135deg, #fff, var(--acc-gold-soft)); }
    .dropzone { min-height: 148px; border: 1px dashed #9ba4b4; border-radius: var(--radius-md); display: grid; place-items: center; text-align: center; padding: 18px; background: rgba(255,255,255,.78); cursor: pointer; }
    .dropzone:hover { background: white; border-color: var(--acc-navy); }
    .dropzone svg { width: 28px; color: var(--acc-navy); margin-bottom: 7px; }
    .dropzone strong { display: block; font-size: .8rem; }
    .dropzone span { display: block; color: var(--text-subtlest); font-size: .68rem; margin-top: 4px; }
    #qrPreview { max-height: 130px; margin: auto; border-radius: 8px; }

    /* Full app views */
    #appShell { display: none; min-height: 100vh; background: var(--surface-sunken); }
    body.app-mode #publicShell, body.app-mode #cmsShell { display: none; }
    body.app-mode #appShell { display: block; }
    .app-topbar { position: sticky; top: 0; z-index: 90; min-height: 68px; background: rgba(255,255,255,.96); border-bottom: 1px solid var(--border); backdrop-filter: blur(16px); }
    .app-topbar__inner { min-height: 68px; display: flex; align-items: center; gap: 18px; }
    .app-topbar__title { min-width: 0; }
    .app-topbar__title strong { display: block; font-size: .9rem; }
    .app-topbar__title span { display: block; color: var(--text-subtlest); font-size: .7rem; }
    .app-topbar__actions { margin-left: auto; display: flex; gap: 8px; }
    .app-view { display: none; }
    .app-view.active { display: block; }
    .app-page { padding: 34px 0 72px; }
    .page-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
    .page-heading h1 { font-size: clamp(1.8rem, 4vw, 2.7rem); line-height: 1.08; letter-spacing: -.04em; margin-bottom: 8px; }
    .page-heading p { margin: 0; color: var(--text-subtle); max-width: 720px; }
    .form-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
    .form-card { border: 1px solid var(--border); background: white; border-radius: var(--radius-lg); box-shadow: var(--shadow-raised); overflow: hidden; }
    .form-card__head { padding: 18px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .form-card__head h2 { margin: 0; font-size: 1.08rem; letter-spacing: -.02em; }
    .form-card__head p { margin: 4px 0 0; color: var(--text-subtlest); font-size: .76rem; }
    .form-card__body { padding: 22px; }
    .form-card__foot { padding: 14px 20px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 9px; background: #fafbfc; }
    .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px 16px; }
    .form-grid .field { margin: 0; }
    .field--full { grid-column: 1 / -1; }
    textarea.input { min-height: 100px; padding-block: 10px; resize: vertical; }
    .help { color: var(--text-subtlest); font-size: .72rem; margin: 4px 0 0; }
    .required { color: var(--danger-text); }
    .stepper { display: grid; grid-template-columns: repeat(4,1fr); gap: 6px; margin-bottom: 20px; }
    .stepper__item { padding: 10px; border-radius: var(--radius-md); background: #ebecf0; color: var(--text-subtlest); font-size: .72rem; font-weight: 750; }
    .stepper__item.active { background: var(--acc-navy); color: white; }
    .stepper__item.done { background: var(--success-bg); color: var(--success-text); }
    .form-step { display: none; }
    .form-step.active { display: block; }
    .summary-card { position: sticky; top: 88px; padding: 20px; border: 1px solid var(--border); border-radius: var(--radius-lg); background: white; box-shadow: var(--shadow-raised); }
    .summary-brand { width: 48px; height: 48px; object-fit: contain; margin-bottom: 14px; }
    .summary-list { display: grid; gap: 12px; margin: 18px 0; }
    .summary-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; padding-bottom: 11px; border-bottom: 1px solid var(--border); font-size: .78rem; }
    .summary-row span { color: var(--text-subtlest); }
    .summary-row strong { text-align: right; }
    .notice { display: flex; align-items: flex-start; gap: 10px; padding: 13px; border-radius: var(--radius-md); background: var(--info-bg); color: #0747a6; font-size: .78rem; }
    .notice svg { flex: 0 0 auto; width: 18px; }
    .notice--warning { background: var(--warning-bg); color: var(--warning-text); }
    .check-row { display: flex; align-items: flex-start; gap: 10px; font-size: .8rem; color: var(--text-subtle); }
    .check-row input { margin-top: 3px; }
    .inventory-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }
    .unit-card { border: 1px solid var(--border); border-radius: var(--radius-lg); background: white; overflow: hidden; box-shadow: var(--shadow-raised); }
    .unit-card img { width: 100%; height: 160px; object-fit: cover; }
    .unit-card__body { padding: 16px; }
    .unit-card__body h3 { margin-bottom: 4px; }
    .unit-card__body p { margin-bottom: 14px; color: var(--text-subtle); font-size: .78rem; }
    .unit-specs { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; margin: 2px 0 14px; color: var(--text-subtle); }
    .unit-spec { min-width: 0; display: inline-flex; align-items: center; gap: 6px; color: var(--text-subtle); }
    .unit-spec:not(:last-child)::after { content: ""; width: 1px; height: 16px; margin-left: 8px; background: var(--border); }
    .unit-spec > svg { width: 17px; height: 17px; flex: 0 0 auto; color: var(--acc-navy); stroke-width: 1.8; }
    .unit-spec span { min-width: 0; display: inline-flex; align-items: baseline; gap: 4px; line-height: 1; }
    .unit-spec strong { font-size: .79rem; color: var(--text); white-space: nowrap; }
    .unit-spec small { color: var(--text-subtlest); font-size: .67rem; font-weight: 650; white-space: nowrap; }
    .unit-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 12px 0; border-block: 1px solid var(--border); margin-bottom: 14px; }
    .unit-meta span { color: var(--text-subtlest); font-size: .68rem; }
    .unit-meta strong { display: block; color: var(--text); font-size: .82rem; margin-top: 2px; }
    .case-hero { padding: 28px; border-radius: var(--radius-xl); background: linear-gradient(135deg,var(--acc-navy),#064a82); color: white; display: grid; grid-template-columns: 1fr auto; gap: 30px; align-items: center; margin-bottom: 20px; }
    .case-hero h1 { font-size: clamp(2rem,5vw,3.4rem); margin: 4px 0 10px; }
    .case-hero p { color: rgba(255,255,255,.72); margin: 0; }
    .case-mark { width: 104px; height: 104px; object-fit: contain; opacity: .9; }
    .payment-grid { display: grid; grid-template-columns: 360px 1fr; gap: 20px; }
    .qr-box { aspect-ratio: 1; border: 1px dashed var(--border-bold); border-radius: var(--radius-lg); display: grid; place-items: center; text-align: center; padding: 26px; background: #fafbfc; }
    .qr-box svg { width: 82px; height: 82px; color: var(--acc-navy); margin-bottom: 14px; }
    .toke-demo-image { width: 100%; border: 1px solid var(--border); border-radius: var(--radius-lg); background: #f7f8fa; box-shadow: var(--shadow-raised); }
    .toke-demo-actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 12px; }
    .toke-demo-actions p { margin: 0; color: var(--text-subtlest); font-size: .72rem; }
    .login-wrap { min-height: calc(100vh - 68px); display: grid; grid-template-columns: minmax(340px,.8fr) 1.2fr; }
    .login-panel { display: grid; place-items: center; padding: 40px; }
    .login-card { width: min(100%,430px); border: 1px solid var(--border); border-radius: var(--radius-xl); background: white; padding: 28px; box-shadow: var(--shadow-overlay); }
    .login-card__logo { width: 62px; height: 62px; object-fit: contain; margin-bottom: 18px; }
    .login-card h1 { font-size: 1.8rem; line-height: 1.12; margin-bottom: 8px; }
    .login-card > p { color: var(--text-subtle); font-size: .86rem; margin-bottom: 24px; }
    .login-art { position: relative; overflow: hidden; background: var(--acc-navy); color: white; display: grid; align-items: end; padding: 54px; }
    .login-art img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: .55; }
    .login-art::after { content:""; position:absolute; inset:0; background:linear-gradient(0deg,rgba(0,20,39,.94),rgba(0,30,58,.18)); }
    .login-art__copy { position: relative; z-index: 1; max-width: 600px; }
    .login-art__copy img { position: static; width: 78px; height: 78px; object-fit: contain; opacity: 1; margin-bottom: 24px; }
    .login-art h2 { font-size: 2.5rem; }
    .login-art p { color: rgba(255,255,255,.75); }
    .portal-layout { display: grid; grid-template-columns: 230px minmax(0,1fr); min-height: calc(100vh - 68px); }
    .portal-side { background: white; border-right: 1px solid var(--border); padding: 20px 14px; }
    .portal-side__brand { display: flex; align-items: center; gap: 10px; padding: 4px 8px 20px; border-bottom: 1px solid var(--border); }
    .portal-side__brand img { width: 38px; height: 38px; object-fit: contain; }
    .portal-side__brand strong { font-size: .78rem; }
    .side-nav { display: grid; gap: 4px; margin-top: 16px; }
    .side-nav button { min-height: 40px; border: 0; border-radius: var(--radius-md); background: transparent; display: flex; align-items: center; gap: 10px; padding: 0 11px; color: var(--text-subtle); cursor: pointer; text-align: left; font-size: .8rem; font-weight: 700; }
    .side-nav button.active, .side-nav button:hover { background: var(--acc-navy-soft); color: var(--acc-navy); }
    .side-nav svg { width: 17px; }
    .portal-content { padding: 28px; min-width: 0; }
    .stage-list { display: grid; gap: 10px; }
    .stage-item { display: grid; grid-template-columns: 38px 1fr auto; align-items: center; gap: 12px; padding: 14px; border: 1px solid var(--border); border-radius: var(--radius-md); }
    .stage-icon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 50%; background: var(--success-bg); color: var(--success-text); }
    .stage-icon.locked { background:#f1f2f4; color:var(--text-subtlest); }
    .stage-item strong { display: block; font-size: .82rem; }
    .stage-item span { color: var(--text-subtlest); font-size: .72rem; }
    .progress { height: 8px; border-radius: 99px; background: #ebecf0; overflow: hidden; }
    .progress > span { display: block; height: 100%; background: var(--acc-navy); border-radius: inherit; }
    .policy-box { max-height: 230px; overflow: auto; padding: 16px; border: 1px solid var(--border); border-radius: var(--radius-md); background: #fafbfc; font-size: .78rem; color: var(--text-subtle); }
    .slot-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
    .slot { min-height: 38px; border: 1px solid var(--border-bold); border-radius: var(--radius-md); background:white; cursor:pointer; color:var(--text-subtle); }
    .slot.selected { background: var(--acc-navy); color:white; border-color:var(--acc-navy); }
    .print-card { padding: 28px; border: 1px solid var(--border); border-radius: var(--radius-lg); background:white; }
    .print-brand { display:flex; align-items:center; gap:12px; padding-bottom:18px; border-bottom:1px solid var(--border); margin-bottom:20px; }
    .print-brand img { width:50px; height:50px; object-fit:contain; }
    .cms-page { display: none; }
    .cms-page.active { display: block; }
    .toolbar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
    .toolbar .input, .toolbar .select { width:auto; min-width:180px; }
    .editor-grid { display:grid; grid-template-columns: minmax(0,1fr) minmax(280px,.45fr); gap:18px; }
    .empty-state { text-align:center; padding:48px 24px; }
    .empty-state .step-icon { margin:0 auto 14px; }
    .portal-panel { display: none; }
    .portal-panel.active { display: block; }
    .portal-panel .page-heading h1 { font-size: clamp(1.7rem,3vw,2.35rem); }
    .portal-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
    .detail-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; margin-bottom:20px; }
    .detail-card { padding:18px; border:1px solid var(--border); border-radius:var(--radius-lg); background:white; box-shadow:var(--shadow-raised); }
    .detail-card span { display:block; color:var(--text-subtlest); font-size:.72rem; margin-bottom:7px; }
    .detail-card strong { display:block; font-size:1rem; }
    .detail-card small { display:block; color:var(--text-subtlest); font-size:.68rem; margin-top:5px; }
    .timeline { display:grid; gap:0; padding:18px; }
    .timeline__item { position:relative; display:grid; grid-template-columns:32px 1fr; gap:12px; padding-bottom:20px; }
    .timeline__item:last-child { padding-bottom:0; }
    .timeline__item:not(:last-child)::after { content:""; position:absolute; left:15px; top:32px; bottom:0; width:2px; background:var(--border); }
    .timeline__dot { width:32px; height:32px; display:grid; place-items:center; border-radius:50%; background:var(--acc-navy-soft); color:var(--acc-navy); z-index:1; }
    .timeline__dot svg { width:16px; }
    .timeline__item strong { display:block; font-size:.8rem; }
    .timeline__item p { margin:3px 0 0; color:var(--text-subtlest); font-size:.72rem; }
    .legal-links { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
    .legal-links button { border:0; background:transparent; color:inherit; padding:0; cursor:pointer; font-size:inherit; }
    .legal-links button:hover { color:white; text-decoration:underline; }
    .app-legal-footer { border-top:1px solid var(--border); background:white; padding:18px 0; color:var(--text-subtlest); font-size:.72rem; }
    .app-legal-footer .container { display:flex; align-items:center; justify-content:space-between; gap:18px; }
    .app-legal-footer .legal-links button:hover { color:var(--acc-navy); }
    .cookie-banner { position:fixed; z-index:998; left:24px; right:24px; bottom:22px; margin:auto; max-width:980px; display:none; grid-template-columns:1fr auto; align-items:center; gap:22px; padding:18px 20px; border:1px solid var(--border); border-radius:var(--radius-lg); background:white; box-shadow:var(--shadow-overlay); }
    .cookie-banner.show { display:grid; }
    .cookie-banner strong { display:block; font-size:.88rem; margin-bottom:4px; }
    .cookie-banner p { margin:0; color:var(--text-subtle); font-size:.76rem; }
    .cookie-banner__actions { display:flex; gap:8px; flex-wrap:wrap; }
    .document-dialog { width:min(96vw,900px); max-height:94vh; }
    .print-sheet { background:white; color:#172b4d; padding:34px; }
    .print-sheet__brand { display:flex; align-items:center; justify-content:space-between; gap:20px; padding-bottom:18px; border-bottom:2px solid var(--acc-gold); }
    .print-sheet__brand > div:first-child { display:flex; align-items:center; gap:12px; }
    .print-sheet__brand img { width:52px; height:52px; object-fit:contain; }
    .print-sheet__brand strong { display:block; font-size:.9rem; }
    .print-sheet__brand span { display:block; color:var(--text-subtlest); font-size:.68rem; }
    .print-sheet h2 { font-size:1.35rem; margin:24px 0 5px; }
    .print-meta { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin:18px 0; }
    .print-meta div { padding:10px; border:1px solid var(--border); border-radius:6px; }
    .print-meta span { display:block; color:var(--text-subtlest); font-size:.65rem; }
    .print-meta strong { display:block; font-size:.78rem; margin-top:3px; }
    .print-sheet table { font-size:.72rem; }
    .print-sheet th, .print-sheet td { padding:8px 9px; }
    .print-total { display:flex; justify-content:flex-end; margin-top:16px; }
    .print-total div { min-width:260px; padding:14px; background:var(--acc-navy-soft); border-radius:8px; }
    .print-total span { color:var(--text-subtlest); font-size:.68rem; }
    .print-total strong { display:block; font-size:1.3rem; margin-top:3px; }
    .print-signatures { display:grid; grid-template-columns:1fr 1fr; gap:60px; margin-top:64px; }
    .print-signatures div { border-top:1px solid #8993a4; text-align:center; padding-top:7px; font-size:.68rem; color:var(--text-subtlest); }
    .print-foot { margin-top:28px; padding-top:12px; border-top:1px solid var(--border); display:flex; justify-content:space-between; gap:20px; color:var(--text-subtlest); font-size:.62rem; }
    .legal-copy h3 { margin:24px 0 7px; font-size:.98rem; }
    .legal-copy p, .legal-copy li { color:var(--text-subtle); font-size:.8rem; }
    .legal-copy ul { padding-left:20px; }

    @media print {
      body * { visibility:hidden !important; }
      body.print-document #printContent, body.print-document #printContent * { visibility:visible !important; }
      body.print-document #printContent { position:absolute; inset:0; width:100%; padding:0; }
      body:not(.print-document) #appointmentPrint, body:not(.print-document) #appointmentPrint * { visibility:visible !important; }
      body:not(.print-document) #appointmentPrint { position:absolute; inset:0; border:0; }
      .print-sheet { box-shadow:none; }
    }

    /* Dialogs and toast */
    dialog { width: min(94vw, 760px); border: 0; border-radius: var(--radius-xl); padding: 0; color: var(--text); box-shadow: var(--shadow-overlay); }
    dialog::backdrop { background: rgba(9,30,66,.62); backdrop-filter: blur(3px); }
    .dialog-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 18px; border-bottom: 1px solid var(--border); }
    .dialog-head h2 { font-size: 1.05rem; margin: 0; letter-spacing: -.02em; }
    .dialog-body { padding: 20px; }
    .project-gallery { margin-bottom: 20px; }
    .project-gallery__stage { overflow: hidden; border-radius: var(--radius-lg); background: #eef1f5; }
    .project-dialog__image { width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block; margin: 0; }
    .project-gallery__thumbs { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(88px, 116px); gap: 10px; margin-top: 10px; overflow-x: auto; padding: 2px 1px 6px; scrollbar-width: thin; }
    .project-gallery__thumbs[hidden] { display: none !important; }
    .project-gallery__thumb { width: 100%; aspect-ratio: 4/3; overflow: hidden; padding: 0; border: 2px solid transparent; border-radius: 10px; background: #eef1f5; cursor: pointer; opacity: .72; transition: border-color .16s ease, opacity .16s ease, transform .16s ease; }
    .project-gallery__thumb:hover { opacity: 1; transform: translateY(-1px); }
    .project-gallery__thumb.is-active { border-color: var(--acc-gold); opacity: 1; }
    .project-gallery__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .dialog-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 18px; border-top: 1px solid var(--border); }
    .portal-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .portal-option { border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px; background: white; cursor: pointer; text-align: left; }
    .portal-option:hover { background: var(--surface-hover); border-color: var(--border-bold); }
    .portal-option .step-icon { margin-bottom: 14px; }
    .portal-option p { color: var(--text-subtle); font-size: .76rem; margin-bottom: 0; }
    .toast { position: fixed; left: 50%; bottom: 28px; z-index: 999; transform: translate(-50%, 20px); opacity: 0; pointer-events: none; max-width: min(92vw, 520px); display: flex; align-items: center; gap: 10px; background: #172b4d; color: white; border-radius: var(--radius-md); padding: 12px 16px; box-shadow: var(--shadow-overlay); font-size: .82rem; font-weight: 650; transition: opacity .18s ease, transform .18s ease; }
    .toast.show { opacity: 1; transform: translate(-50%, 0); }
    .toast svg { width: 18px; color: var(--acc-gold-bright); }

    @media (max-width: 1020px) {
      .nav { display: none; }
      .mobile-menu { display: inline-flex; margin-left: auto; }
      .topbar__actions .btn--subtle { display: none; }
      .hero__content { grid-template-columns: 1fr; gap: 36px; }
      .search-panel { max-width: 620px; }
      .hero { min-height: 820px; }
      .process-grid { grid-template-columns: repeat(2, 1fr); }
      .process-card:nth-child(2)::after { display: none; }
      .service-grid { grid-template-columns: 1fr 1fr; }
      .kpi-grid { grid-template-columns: 1fr 1fr; }
      .cms-grid { grid-template-columns: 1fr; }
      .form-layout, .payment-grid, .editor-grid { grid-template-columns: 1fr; }
      .summary-card { position: static; }
      .inventory-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
      body.cms-mode #cmsShell { grid-template-columns: 78px 1fr; }
      .cms-sidebar { padding-inline: 10px; }
      .cms-brand { justify-content: center; padding-inline: 0; }
      .cms-brand__copy, .cms-nav span, .cms-nav__label, .cms-user > div:last-child { display: none; }
      .cms-nav button { justify-content: center; padding: 0; }
      .cms-user { justify-content: center; padding-inline: 0; }
    }

    @media (min-width: 1021px) {
      .mobile-navigation { display: none !important; }
    }

    @media (max-width: 760px) {
      .container { width: min(calc(100% - 28px), var(--max)); }
      .section { padding: 64px 0; }
      .topbar { height: 64px; }
      .brand { min-width: 0; }
      .brand img { width: 40px; height: 40px; }
      .brand__copy { display:none; }
      .topbar__actions .btn--primary { display: none; }
      .hero { min-height: 760px; }
      .hero__content { padding-top: 76px; }
      .hero__copy h1 { font-size: clamp(2.5rem, 13vw, 4rem); }
      .hero__meta { display: none; }
      .project-grid, .service-grid, .footer-grid { grid-template-columns: 1fr; }
      .process-grid { grid-template-columns: 1fr; }
      .process-card::after { display: none; }
      .section-head, .cta-card { align-items: flex-start; flex-direction: column; }
      .cta-card__actions { width: 100%; flex-wrap: wrap; }
      .project-card__top { flex-direction: column; gap: 8px; }
      .footer-grid { gap: 30px; }
      .footer-bottom { flex-direction: column; }
      .portal-grid { grid-template-columns: 1fr; }
      body.cms-mode #cmsShell { grid-template-columns: 1fr; }
      .cms-sidebar { position: fixed; left: 0; right: 0; bottom: 0; top: auto; z-index: 80; height: 64px; padding: 7px 10px; flex-direction: row; border-top: 1px solid rgba(255,255,255,.2); }
      .cms-brand, .cms-sidebar__bottom, .cms-user, .cms-nav__label { display: none; }
      .cms-nav { width: 100%; margin: 0; display: flex; justify-content: space-around; }
      .cms-nav button { width: 46px; }
      .cms-nav button:nth-of-type(n+6) { display: none; }
      .cms-content { padding: 20px 14px 90px; }
      .cms-topbar { padding: 0 14px; }
      .cms-topbar .btn span { display: none; }
      .cms-heading { align-items: flex-start; flex-direction: column; }
      .kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
      .kpi-card { padding: 14px; }
      .page-heading, .case-hero { align-items:flex-start; grid-template-columns:1fr; flex-direction:column; }
      .case-mark { display:none; }
      .form-grid, .inventory-grid { grid-template-columns:1fr; }
      .stepper { grid-template-columns:1fr 1fr; }
      .login-wrap { grid-template-columns:1fr; }
      .login-art { display:none; }
      .login-panel { padding:24px 14px; }
      .portal-layout { grid-template-columns:1fr; }
      .portal-side { border-right:0; border-bottom:1px solid var(--border); padding:10px 14px; }
      .portal-side__brand { display:none; }
      .side-nav { display:flex; overflow-x:auto; margin:0; }
      .side-nav button { flex:0 0 auto; }
      .portal-content { padding:20px 14px; }
      .slot-grid { grid-template-columns:1fr 1fr; }
      .app-topbar__title span { display:none; }
      .detail-grid, .print-meta { grid-template-columns:1fr; }
      .app-legal-footer .container, .cookie-banner { grid-template-columns:1fr; flex-direction:column; align-items:flex-start; }
      .cookie-banner { left:12px; right:12px; bottom:12px; }
      .cookie-banner__actions { width:100%; }
      .cookie-banner__actions .btn { flex:1; }
    }

    @media (max-width: 480px) {
      .select-row, .kpi-grid { grid-template-columns: 1fr; }
      .hero__actions .btn { width: 100%; }
      .search-panel { padding: 18px; }
      .cta-card__actions .btn { width: 100%; }
      .section-head .btn { width: 100%; }
    }


/* Secure CMS access */
.cms-access-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(9, 30, 66, 0.68);
  backdrop-filter: blur(8px);
}

.cms-access-dialog {
  position: relative;
  width: min(100%, 440px);
  padding: 32px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(9, 30, 66, 0.28);
}

.cms-access-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 10px;
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
}

.cms-access-close:hover { background: var(--surface-subtle); }
.cms-access-close svg { width: 18px; height: 18px; }

.cms-access-icon {
  display: grid;
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
  place-items: center;
  border-radius: 14px;
  color: var(--primary);
  background: var(--primary-soft);
}

.cms-access-icon svg { width: 23px; height: 23px; }
.cms-access-dialog h2 { margin: 4px 0 8px; font-size: 1.55rem; }
.cms-access-copy { margin: 0 0 24px; color: var(--text-muted); line-height: 1.55; }

.cms-access-form {
  display: grid;
  gap: 16px;
}

.cms-access-form label {
  display: grid;
  gap: 7px;
  color: var(--text);
  font-size: .875rem;
  font-weight: 600;
}

.cms-access-password { position: relative; }
.cms-access-password .input { width: 100%; padding-right: 46px; }
.cms-access-password button {
  position: absolute;
  top: 50%;
  right: 6px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  transform: translateY(-50%);
  border: 0;
  border-radius: 8px;
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
}

.cms-access-password button:hover { background: var(--surface-subtle); }
.cms-access-password svg { width: 18px; height: 18px; }

.cms-access-error,
.cms-access-checking {
  padding: 12px 14px;
  border-radius: 10px;
  font-size: .875rem;
  line-height: 1.45;
}

.cms-access-error {
  color: #ae2a19;
  background: #ffebe6;
}

.cms-access-checking {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-muted);
  background: var(--surface-subtle);
}

.cms-access-spinner {
  width: 18px;
  height: 18px;
  animation: cms-access-spin .8s linear infinite;
}

.cms-access-foot {
  margin: 20px 0 0;
  color: var(--text-muted);
  font-size: .78rem;
  line-height: 1.5;
  text-align: center;
}

@keyframes cms-access-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 560px) {
  .unit-specs { gap: 8px 12px; }
  .unit-spec { gap: 5px; }
  .unit-spec:not(:last-child)::after { margin-left: 6px; height: 14px; }
  .unit-spec > svg { width: 16px; height: 16px; }
  .unit-spec strong { font-size: .76rem; }
  .unit-spec small { font-size: .64rem; }

  .cms-access-backdrop { padding: 12px; }
  .cms-access-dialog { padding: 28px 20px 22px; border-radius: 16px; }
}


.connected-availability-grid {
  padding: 24px;
  border-top: 1px solid var(--border);
  grid-template-columns: repeat(auto-fit, minmax(240px, 320px));
  justify-content: start;
}

.connected-availability-grid .cms-data-status {
  grid-column: 1 / -1;
  margin: 0;
}


.honeypot-field {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0 0 0 0) !important;
  clip-path: inset(50%) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.client-payment-portal-card {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: #fff;
}

.client-payment-portal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 8px;
}

.client-payment-portal-head h3 {
  margin: 0;
  font-size: 1.05rem;
}

.client-payment-portal-form {
  display: grid;
  gap: 14px;
  margin-top: 18px;
}

.client-payment-portal-card .notice {
  margin-top: 14px;
}



/* Broker portal experience */
:root {
  --broker-font: "Intro", "Intro Regular", "Avenir Next", "Segoe UI", sans-serif;
  --broker-canvas: #f4f5f7;
  --broker-blue: #0c66e4;
  --broker-blue-hover: #0055cc;
  --broker-navy: #002b52;
  --broker-gold: #b3862e;
  --broker-gold-soft: #fff5d6;
}

#view-broker-login,
#view-broker-register,
#view-broker-portal {
  font-family: var(--broker-font);
}

body.broker-portal-mode .app-topbar__actions {
  display: none;
}

body.broker-portal-mode .app-topbar__title span {
  display: none;
}

body.broker-portal-mode #appShell {
  background: var(--broker-canvas);
}

.broker-login-shell {
  min-height: calc(100vh - 68px);
  display: grid;
  grid-template-columns: minmax(430px, 1.08fr) minmax(380px, .92fr);
  background: var(--broker-canvas);
}

.broker-login-intro {
  position: relative;
  display: grid;
  align-items: center;
  overflow: hidden;
  padding: clamp(48px, 7vw, 96px);
  color: #fff;
  background:
    linear-gradient(135deg, rgba(0, 27, 53, .98), rgba(0, 61, 107, .9)),
    url("/assets/prestige-ii.webp") center/cover;
}

.broker-login-intro::before,
.broker-login-intro::after {
  position: absolute;
  content: "";
  border-radius: 50%;
  pointer-events: none;
}

.broker-login-intro::before {
  width: 420px;
  height: 420px;
  right: -190px;
  top: -160px;
  border: 64px solid rgba(245, 215, 139, .08);
}

.broker-login-intro::after {
  width: 260px;
  height: 260px;
  left: -160px;
  bottom: -130px;
  background: rgba(12, 102, 228, .16);
}

.broker-login-intro__content {
  position: relative;
  z-index: 1;
  max-width: 620px;
}

.broker-login-intro__logo {
  width: 68px;
  height: 68px;
  object-fit: contain;
  margin-bottom: 36px;
}

.broker-login-intro .eyebrow {
  color: var(--acc-gold-bright);
}

.broker-login-intro h1 {
  max-width: 600px;
  margin-bottom: 20px;
  font-size: clamp(2.45rem, 4.5vw, 4.7rem);
  line-height: 1.02;
}

.broker-login-intro__content > p:not(.eyebrow) {
  max-width: 560px;
  color: rgba(255, 255, 255, .78);
  font-size: 1.05rem;
}

.broker-feature-list {
  display: grid;
  gap: 12px;
  max-width: 540px;
  margin: 36px 0 0;
  padding: 0;
  list-style: none;
}

.broker-feature-list li {
  display: grid;
  grid-template-columns: 42px 1fr;
  align-items: center;
  gap: 13px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 255, 255, .14);
  border-radius: 10px;
  background: rgba(255, 255, 255, .07);
  backdrop-filter: blur(8px);
}

.broker-feature-list li > span {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 8px;
  color: var(--acc-gold-bright);
  background: rgba(245, 215, 139, .1);
}

.broker-feature-list svg {
  width: 19px;
}

.broker-feature-list strong,
.broker-feature-list small {
  display: block;
}

.broker-feature-list strong {
  font-size: .88rem;
}

.broker-feature-list small {
  margin-top: 2px;
  color: rgba(255, 255, 255, .64);
  font-size: .74rem;
}

.broker-login-panel {
  display: grid;
  place-items: center;
  padding: 48px;
}

.broker-login-card {
  width: min(100%, 460px);
  padding: 36px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(9, 30, 66, .12), 0 0 1px rgba(9, 30, 66, .22);
}

.broker-auth-brand {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 30px;
  color: var(--broker-navy);
  font-size: .78rem;
  font-weight: 800;
}

.broker-auth-brand img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.broker-login-card h2 {
  margin-bottom: 8px;
  font-size: 1.75rem;
  letter-spacing: -.025em;
}

.broker-auth-copy {
  margin-bottom: 24px;
  color: var(--text-subtle);
  font-size: .88rem;
}

.broker-auth-submit {
  margin-top: 8px;
  background: var(--broker-blue);
}

.broker-auth-submit:hover {
  background: var(--broker-blue-hover);
}

.broker-auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 8px;
  color: var(--text-subtlest);
  font-size: .72rem;
}

.broker-auth-divider::before,
.broker-auth-divider::after {
  height: 1px;
  flex: 1;
  content: "";
  background: var(--border);
}

.broker-auth-help {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 20px 0 0;
  color: var(--text-subtlest);
  font-size: .72rem;
  line-height: 1.5;
}

.broker-auth-help svg {
  width: 15px;
  flex: 0 0 auto;
  margin-top: 1px;
  color: var(--broker-blue);
}

.broker-register-view {
  background:
    radial-gradient(circle at 92% 5%, rgba(12, 102, 228, .08), transparent 26rem),
    var(--broker-canvas);
}

.broker-register-page {
  padding: 30px 0 72px;
}

.broker-register-heading > .btn {
  margin-bottom: 22px;
  margin-left: -12px;
}

.broker-register-title {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 26px;
}

.broker-register-title h1 {
  margin-bottom: 9px;
  font-size: clamp(2rem, 4vw, 3rem);
  letter-spacing: -.04em;
}

.broker-register-title p {
  max-width: 720px;
  margin: 0;
  color: var(--text-subtle);
}

.broker-register-layout {
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
}

.broker-register-form,
.broker-register-summary {
  border-radius: 12px;
}

.broker-register-form .form-card__head {
  padding: 22px 24px;
}

.broker-register-form .form-card__body {
  padding: 26px 24px;
}

.broker-register-form .form-card__foot {
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
}

.broker-section-number {
  display: block;
  margin-bottom: 7px;
  color: var(--broker-gold);
  font-size: .68rem;
  font-weight: 900;
  letter-spacing: .12em;
}

.broker-card-icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 10px;
  color: var(--broker-blue);
  background: var(--info-bg);
}

.broker-card-icon--gold {
  margin-bottom: 18px;
  color: var(--broker-gold);
  background: var(--broker-gold-soft);
}

.broker-card-icon svg {
  width: 20px;
}

.broker-policy-field {
  margin-top: 6px !important;
}

.broker-policy-check {
  padding: 14px;
  border-radius: 8px;
  background: var(--surface-sunken);
}

.broker-policy-check input {
  width: 17px;
  height: 17px;
  accent-color: var(--broker-blue);
}

.broker-form-footnote {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--text-subtlest);
  font-size: .72rem;
}

.broker-form-footnote svg {
  width: 15px;
}

.broker-register-summary {
  top: 96px;
  padding: 26px;
}

.broker-register-summary h2 {
  margin-bottom: 22px;
  font-size: 1.35rem;
}

.broker-register-summary .stage-item {
  grid-template-columns: 36px 1fr auto;
  padding: 13px 0;
  border: 0;
  border-bottom: 1px solid var(--border);
  border-radius: 0;
}

.broker-register-summary .stage-item:last-child {
  border-bottom: 0;
}

.broker-register-summary .stage-count {
  color: var(--text-subtlest);
  font-size: .68rem;
  font-weight: 800;
}

.broker-register-notice {
  margin-top: 22px;
  background: var(--broker-gold-soft);
  color: #6b4d00;
}

.broker-experience .portal-layout {
  grid-template-columns: 252px minmax(0, 1fr);
  min-height: calc(100vh - 68px);
}

.broker-experience .portal-side {
  position: sticky;
  top: 68px;
  display: flex;
  height: calc(100vh - 68px);
  flex-direction: column;
  padding: 20px 14px 16px;
  border-right-color: #dfe1e6;
  background: #fff;
}

.broker-experience .portal-side__brand {
  padding: 3px 8px 20px;
}

.broker-experience .portal-side__brand > div {
  min-width: 0;
}

.broker-experience .portal-side__brand strong,
.broker-experience .portal-side__brand span {
  display: block;
}

.broker-experience .portal-side__brand strong {
  color: var(--broker-navy);
  font-size: .82rem;
}

.broker-experience .portal-side__brand span {
  margin-top: 2px;
  color: var(--text-subtlest);
  font-size: .66rem;
}

.broker-experience .side-nav {
  gap: 5px;
}

.broker-experience .side-nav button {
  position: relative;
  min-height: 42px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: .82rem;
}

.broker-experience .side-nav button:hover {
  color: var(--broker-navy);
  background: #f1f2f4;
}

.broker-experience .side-nav button.active {
  color: #0c417a;
  background: #e9f2ff;
}

.broker-experience .side-nav button.active::before {
  position: absolute;
  left: 0;
  width: 3px;
  height: 22px;
  border-radius: 0 3px 3px 0;
  content: "";
  background: var(--broker-blue);
}

.broker-experience .side-nav button:focus-visible {
  outline: 2px solid var(--broker-blue);
  outline-offset: 1px;
}

.portal-side__footer {
  display: grid;
  gap: 10px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.portal-session-status {
  display: grid;
  grid-template-columns: 8px 1fr;
  align-items: center;
  gap: 9px;
  padding: 0 10px;
}

.portal-session-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22a06b;
  box-shadow: 0 0 0 3px #dcfff1;
}

.portal-session-status strong,
.portal-session-status small {
  display: block;
}

.portal-session-status strong {
  font-size: .72rem;
}

.portal-session-status small {
  color: var(--text-subtlest);
  font-size: .64rem;
}

.portal-logout {
  justify-content: flex-start;
  color: #ae2a19;
}

.portal-logout:hover {
  color: #ae2a19;
  background: #ffebe6;
}

.broker-experience .portal-content {
  padding: 34px clamp(22px, 3vw, 44px) 56px;
  background:
    radial-gradient(circle at 95% 0, rgba(12, 102, 228, .055), transparent 28rem),
    var(--broker-canvas);
}

.broker-experience .page-heading {
  align-items: center;
  margin-bottom: 26px;
}

.broker-experience .page-heading h1 {
  color: var(--broker-navy);
  font-size: clamp(1.8rem, 3vw, 2.45rem);
}

.broker-experience .page-heading p {
  font-size: .88rem;
}

.broker-experience .kpi-grid {
  gap: 14px;
}

.broker-experience .kpi-card {
  position: relative;
  overflow: hidden;
  padding: 18px;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(9, 30, 66, .1);
}

.broker-experience .kpi-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  content: "";
  background: var(--broker-blue);
}

.broker-experience .kpi-card:nth-child(3)::before,
.broker-experience .kpi-card:nth-child(4)::before {
  background: var(--broker-gold);
}

.broker-experience .kpi-card:nth-child(3) .kpi-icon,
.broker-experience .kpi-card:nth-child(4) .kpi-icon {
  color: var(--broker-gold);
  background: var(--broker-gold-soft);
}

.broker-experience .kpi-value {
  color: var(--broker-navy);
  font-variant-numeric: tabular-nums;
}

.broker-experience .panel,
.broker-experience .form-card,
.broker-experience .detail-card {
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(9, 30, 66, .09);
}

.broker-experience .panel__head {
  min-height: 64px;
  padding: 15px 18px;
  background: #fff;
}

.broker-experience .panel__head h3 {
  color: var(--broker-navy);
  font-size: .95rem;
}

.broker-experience table th {
  color: var(--text-subtlest);
  background: #f7f8f9;
  font-size: .68rem;
  letter-spacing: .035em;
  text-transform: uppercase;
}

.broker-experience table td {
  font-size: .8rem;
}

.broker-experience .form-card__head h2 {
  color: var(--broker-navy);
}

.broker-experience .input,
.broker-experience .select,
.broker-auth-view .input,
.broker-auth-view .select {
  border-color: #b7bdc8;
  border-radius: 6px;
}

.broker-experience .input:focus,
.broker-experience .select:focus,
.broker-auth-view .input:focus,
.broker-auth-view .select:focus {
  border-color: var(--broker-blue);
  box-shadow: 0 0 0 2px rgba(12, 102, 228, .2);
}

.broker-experience .field label,
.broker-auth-view .field label {
  color: #172b4d;
  font-size: .76rem;
}

.broker-experience .btn--primary,
.broker-register-view .btn--primary {
  background: var(--broker-blue);
}

.broker-experience .btn--primary:hover,
.broker-register-view .btn--primary:hover {
  background: var(--broker-blue-hover);
}

.portal-notification-region {
  position: fixed;
  z-index: 1300;
  top: 84px;
  right: 20px;
  display: grid;
  width: min(calc(100vw - 32px), 390px);
  gap: 10px;
  pointer-events: none;
}

.portal-notification {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 30px;
  align-items: start;
  gap: 11px;
  padding: 14px;
  border: 1px solid var(--border);
  border-left: 4px solid var(--broker-blue);
  border-radius: 8px;
  color: var(--text);
  background: #fff;
  box-shadow: var(--shadow-overlay);
  pointer-events: auto;
  animation: portal-notification-in .22s ease both;
}

.portal-notification--success { border-left-color: #22a06b; }
.portal-notification--error { border-left-color: #c9372c; }
.portal-notification--warning { border-left-color: #f5cd47; }

.portal-notification__icon {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 8px;
  color: var(--broker-blue);
  background: var(--info-bg);
}

.portal-notification--success .portal-notification__icon {
  color: #216e4e;
  background: var(--success-bg);
}

.portal-notification--error .portal-notification__icon {
  color: #ae2a19;
  background: var(--danger-bg);
}

.portal-notification--warning .portal-notification__icon {
  color: #7f5f01;
  background: var(--warning-bg);
}

.portal-notification__icon svg {
  width: 18px;
}

.portal-notification__copy strong,
.portal-notification__copy span {
  display: block;
}

.portal-notification__copy strong {
  margin: 1px 0 3px;
  font-size: .82rem;
}

.portal-notification__copy span {
  color: var(--text-subtle);
  font-size: .74rem;
  line-height: 1.45;
}

.portal-notification__close {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border: 0;
  border-radius: 6px;
  color: var(--text-subtlest);
  background: transparent;
  cursor: pointer;
}

.portal-notification__close:hover {
  background: var(--surface-hover);
}

.portal-notification__close svg {
  width: 16px;
}

.portal-notification.is-leaving {
  animation: portal-notification-out .18s ease both;
}

@keyframes portal-notification-in {
  from { opacity: 0; transform: translateY(-8px) translateX(12px); }
}

@keyframes portal-notification-out {
  to { opacity: 0; transform: translateX(16px); }
}

@media (max-width: 900px) {
  .broker-login-shell {
    grid-template-columns: 1fr;
  }

  .broker-login-intro {
    min-height: auto;
    padding: 46px 28px;
  }

  .broker-login-intro h1 {
    font-size: clamp(2rem, 8vw, 3.3rem);
  }

  .broker-login-intro__logo,
  .broker-feature-list {
    display: none;
  }

  .broker-login-panel {
    padding: 32px 18px 56px;
  }

  .broker-register-layout {
    grid-template-columns: 1fr;
  }

  .broker-register-summary {
    position: static;
    order: -1;
  }
}

@media (max-width: 760px) {
  body.broker-portal-mode .app-topbar__inner {
    gap: 10px;
  }

  body.broker-portal-mode .app-topbar__inner > img,
  body.broker-portal-mode .app-topbar__title {
    display: none;
  }

  .broker-experience .portal-layout {
    grid-template-columns: 1fr;
  }

  .broker-experience .portal-side {
    position: sticky;
    top: 68px;
    z-index: 60;
    display: block;
    width: 100%;
    height: auto;
    padding: 8px 14px;
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }

  .broker-experience .portal-side__brand,
  .broker-experience .portal-session-status {
    display: none;
  }

  .broker-experience .side-nav {
    display: flex;
    margin: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .broker-experience .side-nav::-webkit-scrollbar {
    display: none;
  }

  .broker-experience .side-nav button {
    flex: 0 0 auto;
  }

  .broker-experience .side-nav button.active::before {
    inset: auto 10px 0;
    width: auto;
    height: 3px;
    border-radius: 3px 3px 0 0;
  }

  .portal-side__footer {
    position: absolute;
    top: -58px;
    right: 14px;
    display: block;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .portal-logout {
    min-height: 38px;
    padding-inline: 11px;
    background: #ffebe6;
  }

  .broker-experience .portal-content {
    padding: 24px 14px 48px;
  }

  .broker-register-title {
    align-items: flex-start;
    flex-direction: column;
  }

  .broker-register-form .form-card__foot {
    align-items: stretch;
    flex-direction: column;
  }

  .broker-form-footnote {
    justify-content: center;
  }

  .portal-notification-region {
    top: 76px;
    right: 12px;
  }
}

@media (max-width: 480px) {
  .broker-login-card {
    padding: 26px 20px;
  }

  .broker-login-intro {
    padding: 38px 22px;
  }

  .broker-register-page {
    padding-top: 18px;
  }

  .broker-register-summary {
    padding: 20px;
  }

  .broker-experience .kpi-grid {
    grid-template-columns: 1fr;
  }

  .portal-logout {
    font-size: 0;
  }

  .portal-logout svg {
    width: 19px;
    margin: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .portal-notification {
    animation: none;
  }
}


/* Client portal mobile-first experience */
#view-client-login,
#view-client-portal {
  font-family: var(--broker-font);
}

body.client-portal-mode .app-topbar__actions {
  display: none;
}

body.client-portal-mode .app-topbar__title span {
  display: none;
}

body.client-portal-mode #appShell {
  background: var(--broker-canvas);
}

.client-login-shell {
  min-height: calc(100vh - 68px);
  display: grid;
  grid-template-columns: minmax(430px, 1.08fr) minmax(380px, .92fr);
  background: var(--broker-canvas);
}

.client-login-intro {
  position: relative;
  display: grid;
  align-items: center;
  overflow: hidden;
  padding: clamp(48px, 7vw, 96px);
  color: #fff;
  background:
    linear-gradient(135deg, rgba(0, 27, 53, .97), rgba(0, 70, 122, .86)),
    url("/assets/towers.webp") center/cover;
}

.client-login-intro::after {
  position: absolute;
  right: -140px;
  bottom: -190px;
  width: 430px;
  height: 430px;
  border: 70px solid rgba(245, 215, 139, .09);
  border-radius: 50%;
  content: "";
}

.client-login-intro__content {
  position: relative;
  z-index: 1;
  max-width: 620px;
}

.client-login-intro__logo {
  width: 68px;
  height: 68px;
  object-fit: contain;
  margin-bottom: 36px;
}

.client-login-intro .eyebrow {
  color: var(--acc-gold-bright);
}

.client-login-intro h1 {
  max-width: 600px;
  margin-bottom: 20px;
  font-size: clamp(2.45rem, 4.5vw, 4.7rem);
  line-height: 1.02;
}

.client-login-intro__content > p:not(.eyebrow) {
  max-width: 560px;
  color: rgba(255, 255, 255, .78);
  font-size: 1.05rem;
}

.client-mobile-preview {
  display: grid;
  grid-template-columns: 46px 1fr;
  align-items: center;
  gap: 14px;
  max-width: 480px;
  margin-top: 34px;
  padding: 15px;
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 10px;
  background: rgba(255,255,255,.08);
  backdrop-filter: blur(8px);
}

.client-mobile-preview > span {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 9px;
  color: var(--acc-gold-bright);
  background: rgba(245,215,139,.1);
}

.client-mobile-preview svg {
  width: 20px;
}

.client-mobile-preview strong,
.client-mobile-preview small {
  display: block;
}

.client-mobile-preview strong {
  font-size: .88rem;
}

.client-mobile-preview small {
  margin-top: 3px;
  color: rgba(255,255,255,.65);
  font-size: .74rem;
}

.client-login-panel {
  display: grid;
  place-items: center;
  padding: 48px;
}

.client-login-card {
  width: min(100%,460px);
  padding: 36px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(9,30,66,.12), 0 0 1px rgba(9,30,66,.22);
}

.client-login-card h2 {
  margin-bottom: 8px;
  font-size: 1.75rem;
  letter-spacing: -.025em;
}

.client-auth-submit {
  margin-top: 8px;
  background: var(--broker-blue);
}

.client-auth-submit:hover {
  background: var(--broker-blue-hover);
}

.client-experience .portal-layout {
  grid-template-columns: 252px minmax(0,1fr);
  min-height: calc(100vh - 68px);
}

.client-experience .portal-side {
  position: sticky;
  top: 68px;
  display: flex;
  height: calc(100vh - 68px);
  flex-direction: column;
  padding: 20px 14px 16px;
  background: #fff;
}

.client-experience .portal-side__brand > div,
.client-experience .portal-side__brand strong,
.client-experience .portal-side__brand span {
  display: block;
}

.client-experience .portal-side__brand strong {
  color: var(--broker-navy);
  font-size: .82rem;
}

.client-experience .portal-side__brand span {
  margin-top: 2px;
  color: var(--text-subtlest);
  font-size: .66rem;
}

.client-experience .side-nav {
  gap: 5px;
}

.client-experience .side-nav button {
  position: relative;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: .82rem;
}

.client-experience .side-nav button:hover {
  color: var(--broker-navy);
  background: #f1f2f4;
}

.client-experience .side-nav button.active {
  color: #0c417a;
  background: #e9f2ff;
}

.client-experience .side-nav button.active::before {
  position: absolute;
  left: 0;
  width: 3px;
  height: 22px;
  border-radius: 0 3px 3px 0;
  content: "";
  background: var(--broker-blue);
}

.client-experience .portal-content {
  padding: 34px clamp(22px,3vw,44px) 60px;
  background:
    radial-gradient(circle at 95% 0, rgba(12,102,228,.055), transparent 28rem),
    var(--broker-canvas);
}

.client-experience .page-heading {
  align-items: center;
  margin-bottom: 26px;
}

.client-experience .page-heading h1 {
  color: var(--broker-navy);
  font-size: clamp(1.8rem,3vw,2.45rem);
}

.client-experience .page-heading p {
  font-size: .88rem;
}

.client-property-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: 22px 24px;
  overflow: hidden;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(120deg,var(--broker-navy),#064f86);
  box-shadow: 0 8px 20px rgba(0,36,70,.14);
}

.client-property-hero__copy {
  display: flex;
  align-items: center;
  gap: 16px;
}

.client-property-icon {
  display: grid;
  width: 50px;
  height: 50px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 12px;
  color: var(--acc-gold-bright);
  background: rgba(255,255,255,.1);
}

.client-property-icon svg {
  width: 24px;
}

.client-property-hero p,
.client-property-hero h2,
.client-property-hero span {
  margin: 0;
}

.client-property-hero__copy p,
.client-property-hero__meta span {
  color: rgba(255,255,255,.64);
  font-size: .7rem;
}

.client-property-hero h2 {
  margin: 3px 0;
  font-size: 1.35rem;
}

.client-property-hero__copy > div > span {
  color: rgba(255,255,255,.82);
  font-size: .82rem;
}

.client-property-hero__meta {
  display: flex;
  gap: 28px;
}

.client-property-hero__meta div {
  min-width: 110px;
}

.client-property-hero__meta strong {
  display: block;
  margin-top: 3px;
  font-size: .82rem;
}

.client-kpi-grid {
  gap: 14px;
}

.client-experience .kpi-card {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(9,30,66,.09);
}

.client-experience .kpi-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  content: "";
  background: var(--broker-blue);
}

.client-experience .kpi-card:nth-child(3)::before,
.client-experience .kpi-card:nth-child(4)::before {
  background: var(--broker-gold);
}

.client-experience .kpi-card:nth-child(3) .kpi-icon,
.client-experience .kpi-card:nth-child(4) .kpi-icon {
  color: var(--broker-gold);
  background: var(--broker-gold-soft);
}

.client-experience .kpi-value {
  color: var(--broker-navy);
  font-variant-numeric: tabular-nums;
}

.client-kpi-status {
  font-size: 1.22rem;
}

.client-dashboard-grid,
.client-proof-layout,
.client-appointment-layout {
  display: grid;
  grid-template-columns: minmax(0,1.45fr) minmax(310px,.75fr);
  gap: 18px;
}

.client-next-action__body {
  display: grid;
  grid-template-columns: 44px minmax(0,1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 22px;
}

.client-action-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 10px;
  color: var(--broker-blue);
  background: var(--info-bg);
}

.client-action-icon svg {
  width: 20px;
}

.client-next-action__body strong {
  display: block;
  font-size: .88rem;
}

.client-next-action__body p {
  margin: 3px 0 0;
  color: var(--text-subtle);
  font-size: .75rem;
}

.client-experience .panel,
.client-experience .form-card,
.client-experience .detail-card {
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(9,30,66,.09);
}

.client-experience .panel__head {
  min-height: 64px;
  padding: 15px 18px;
}

.client-experience .panel__head h3,
.client-experience .form-card__head h2 {
  color: var(--broker-navy);
}

.client-experience .btn--primary {
  background: var(--broker-blue);
}

.client-experience .btn--primary:hover {
  background: var(--broker-blue-hover);
}

.client-experience .input,
.client-experience .select,
.client-auth-view .input {
  border-color: #b7bdc8;
  border-radius: 6px;
}

.client-experience .input:focus,
.client-experience .select:focus,
.client-auth-view .input:focus {
  border-color: var(--broker-blue);
  box-shadow: 0 0 0 2px rgba(12,102,228,.2);
}

.client-plan-summary {
  margin-bottom: 18px;
}

.client-stage-cards {
  display: grid;
  grid-template-columns: repeat(4,minmax(0,1fr));
  gap: 12px;
  padding: 18px;
}

.client-stage-card {
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: #fff;
}

.client-stage-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.client-stage-card h4 {
  margin: 0;
  color: var(--broker-navy);
  font-size: .82rem;
}

.client-stage-card p {
  margin: 4px 0 12px;
  color: var(--text-subtlest);
  font-size: .68rem;
}

.client-stage-card__amounts {
  display: grid;
  gap: 5px;
  padding-top: 11px;
  border-top: 1px solid var(--border);
}

.client-stage-card__amounts div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: .7rem;
}

.client-stage-card__amounts span {
  color: var(--text-subtlest);
}

.client-mobile-list {
  display: none;
}

.client-proof-dropzone {
  min-height: 126px;
}

.client-submission-list,
.client-appointment-list {
  display: grid;
  gap: 0;
}

.client-submission,
.client-appointment-item {
  display: grid;
  grid-template-columns: 38px minmax(0,1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 15px 18px;
  border-bottom: 1px solid var(--border);
}

.client-submission:last-child,
.client-appointment-item:last-child {
  border-bottom: 0;
}

.client-list-icon {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 9px;
  color: var(--broker-blue);
  background: var(--info-bg);
}

.client-list-icon svg {
  width: 17px;
}

.client-submission strong,
.client-appointment-item strong {
  display: block;
  font-size: .8rem;
}

.client-submission p,
.client-appointment-item p {
  margin: 3px 0 0;
  color: var(--text-subtlest);
  font-size: .7rem;
}

.client-progress-summary {
  padding: 22px 22px 10px;
}

.client-progress-summary > div:first-child {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.client-progress-summary span {
  color: var(--text-subtle);
  font-size: .78rem;
}

.client-progress-summary strong {
  color: var(--broker-navy);
  font-size: 1.45rem;
}

.client-progress-panel .timeline {
  padding-top: 14px;
}

.client-experience table th {
  color: var(--text-subtlest);
  background: #f7f8f9;
  font-size: .68rem;
  letter-spacing: .035em;
  text-transform: uppercase;
}

.client-experience table td {
  font-size: .8rem;
}

@media (max-width: 1100px) {
  .client-stage-cards {
    grid-template-columns: repeat(2,minmax(0,1fr));
  }

  .client-dashboard-grid,
  .client-proof-layout,
  .client-appointment-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .client-login-shell {
    grid-template-columns: 1fr;
  }

  .client-login-intro {
    min-height: 310px;
    padding: 46px 28px;
  }

  .client-login-intro h1 {
    font-size: clamp(2rem,8vw,3.3rem);
  }

  .client-login-intro__logo {
    display: none;
  }

  .client-login-panel {
    padding: 32px 18px 56px;
  }
}

@media (max-width: 760px) {
  body.client-portal-mode .app-topbar {
    min-height: 58px;
  }

  body.client-portal-mode .app-topbar__inner {
    min-height: 58px;
  }

  body.client-portal-mode .app-topbar__inner > img,
  body.client-portal-mode .app-topbar__title {
    display: none;
  }

  .client-experience .portal-layout {
    display: block;
    min-height: calc(100vh - 58px);
  }

  .client-experience .portal-side {
    position: fixed;
    z-index: 100;
    inset: auto 0 0;
    display: block;
    width: 100%;
    height: 72px;
    padding: 6px 6px calc(6px + env(safe-area-inset-bottom));
    border: 0;
    border-top: 1px solid var(--border);
    background: rgba(255,255,255,.97);
    box-shadow: 0 -4px 16px rgba(9,30,66,.08);
    backdrop-filter: blur(16px);
  }

  .client-experience .portal-side__brand,
  .client-experience .portal-session-status {
    display: none;
  }

  .client-experience .client-side-nav {
    display: grid;
    grid-template-columns: repeat(6,minmax(56px,1fr));
    gap: 2px;
    margin: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .client-experience .client-side-nav::-webkit-scrollbar {
    display: none;
  }

  .client-experience .client-side-nav button {
    min-height: 58px;
    flex-direction: column;
    gap: 3px;
    padding: 5px 3px;
    border-radius: 8px;
    font-size: .6rem;
  }

  .client-experience .client-side-nav button svg {
    width: 19px;
    height: 19px;
  }

  .client-experience .client-side-nav button.active::before {
    inset: 0 14px auto;
    width: auto;
    height: 3px;
    border-radius: 0 0 3px 3px;
  }

  .client-experience .portal-side__footer {
    position: fixed;
    z-index: 110;
    top: 10px;
    right: 12px;
    display: block;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .client-experience .portal-logout {
    min-height: 38px;
    padding-inline: 11px;
    background: #ffebe6;
  }

  .client-experience .portal-content {
    padding: 22px 14px calc(96px + env(safe-area-inset-bottom));
  }

  .client-experience .page-heading {
    align-items: flex-start;
  }

  .client-experience .page-heading h1 {
    font-size: 1.7rem;
  }

  .client-welcome .lozenge {
    margin-top: -8px;
  }

  .client-property-hero {
    align-items: flex-start;
    flex-direction: column;
    padding: 18px;
  }

  .client-property-hero__meta {
    width: 100%;
    gap: 10px;
  }

  .client-property-hero__meta div {
    min-width: 0;
    flex: 1;
    padding-top: 12px;
    border-top: 1px solid rgba(255,255,255,.14);
  }

  .client-kpi-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
  }

  .client-kpi-grid .kpi-card {
    min-height: 132px;
    padding: 14px;
  }

  .client-kpi-grid .kpi-value {
    font-size: 1.35rem;
  }

  .client-next-action__body {
    grid-template-columns: 40px minmax(0,1fr);
    padding: 17px;
  }

  .client-next-action__body .btn {
    grid-column: 1 / -1;
    width: 100%;
  }

  .client-stage-cards {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .client-installments-panel .table-wrap,
  .client-receipts-panel .table-wrap {
    display: none;
  }

  .client-mobile-list {
    display: grid;
  }

  .client-mobile-card {
    display: grid;
    gap: 9px;
    padding: 16px;
    border-bottom: 1px solid var(--border);
  }

  .client-mobile-card:last-child {
    border-bottom: 0;
  }

  .client-mobile-card__top,
  .client-mobile-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .client-mobile-card__top strong {
    color: var(--broker-navy);
    font-size: .85rem;
  }

  .client-mobile-card__row {
    color: var(--text-subtlest);
    font-size: .7rem;
  }

  .client-mobile-card__row strong {
    color: var(--text);
    font-size: .73rem;
  }

  .client-mobile-card .btn {
    width: 100%;
    margin-top: 3px;
  }

  .client-proof-layout .form-card__body,
  .client-appointment-layout .form-card__body {
    padding: 18px;
  }

  .client-proof-layout .form-card__foot,
  .client-appointment-layout .form-card__foot {
    padding: 12px 18px;
  }

  .client-proof-layout .form-card__foot .btn,
  .client-appointment-layout .form-card__foot .btn {
    width: 100%;
  }

  #clientNotificationRegion {
    top: 70px;
  }
}

@media (max-width: 480px) {
  .client-login-intro {
    min-height: 275px;
    padding: 34px 22px;
  }

  .client-login-card {
    padding: 26px 20px;
  }

  .client-mobile-preview {
    margin-top: 22px;
  }

  .client-kpi-grid {
    gap: 9px;
  }

  .client-kpi-grid .kpi-card {
    min-height: 124px;
  }

  .client-experience .portal-logout {
    font-size: 0;
  }

  .client-experience .portal-logout svg {
    width: 19px;
  }
}

/* Portal print + mobile hardening */
.broker-mobile-list {
  display: none;
}

.broker-mobile-card {
  display: grid;
  gap: 10px;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  background: #fff;
}

.broker-mobile-card:last-child {
  border-bottom: 0;
}

.broker-mobile-card__top,
.broker-mobile-card__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.broker-mobile-card__top strong {
  min-width: 0;
  color: var(--broker-navy);
  font-size: .88rem;
  overflow-wrap: anywhere;
}

.broker-mobile-card__row {
  color: var(--text-subtlest);
  font-size: .72rem;
}

.broker-mobile-card__row strong {
  max-width: 62%;
  color: var(--text);
  font-size: .76rem;
  text-align: right;
  overflow-wrap: anywhere;
}

.broker-mobile-card .btn {
  min-height: 42px;
  margin-top: 3px;
}

.broker-mobile-empty {
  padding: 18px;
}

@media (max-width: 760px) {
  body.client-portal-mode,
  body.broker-portal-mode {
    overflow-x: hidden;
  }

  body.client-portal-mode .app-topbar,
  body.broker-portal-mode .app-topbar {
    min-height: 58px;
  }

  body.client-portal-mode .app-topbar__inner,
  body.broker-portal-mode .app-topbar__inner {
    min-height: 58px;
    padding-inline: 14px 62px;
  }

  body.client-portal-mode .app-topbar__inner > img,
  body.broker-portal-mode .app-topbar__inner > img {
    display: block;
    width: 34px;
    height: 34px;
  }

  body.client-portal-mode .app-topbar__title,
  body.broker-portal-mode .app-topbar__title {
    display: block;
    min-width: 0;
  }

  body.client-portal-mode .app-topbar__title strong,
  body.broker-portal-mode .app-topbar__title strong {
    display: block;
    max-width: 190px;
    overflow: hidden;
    font-size: .78rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .client-experience .page-heading,
  .broker-experience .page-heading {
    display: flex;
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 18px;
  }

  .client-experience .page-heading > .btn,
  .broker-experience .page-heading > .btn,
  .client-experience .page-heading .portal-actions,
  .broker-experience .page-heading .portal-actions {
    width: 100%;
  }

  .client-experience .page-heading > .btn,
  .broker-experience .page-heading > .btn,
  .client-experience .portal-actions .btn,
  .broker-experience .portal-actions .btn {
    min-height: 44px;
    width: 100%;
  }

  .broker-experience .portal-layout {
    display: block;
    min-height: calc(100vh - 58px);
  }

  .broker-experience .portal-side {
    position: fixed;
    z-index: 100;
    inset: auto 0 0;
    width: 100%;
    height: 72px;
    padding: 6px 6px calc(6px + env(safe-area-inset-bottom));
    border: 0;
    border-top: 1px solid var(--border);
    background: rgba(255,255,255,.97);
    box-shadow: 0 -4px 16px rgba(9,30,66,.08);
    backdrop-filter: blur(16px);
  }

  .broker-experience .side-nav {
    display: grid;
    grid-template-columns: repeat(5,minmax(58px,1fr));
    gap: 2px;
    margin: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .broker-experience .side-nav button {
    min-height: 58px;
    flex-direction: column;
    gap: 3px;
    justify-content: center;
    padding: 5px 3px;
    border-radius: 8px;
    font-size: .58rem;
    text-align: center;
  }

  .broker-experience .side-nav button svg {
    width: 19px;
    height: 19px;
  }

  .broker-experience .side-nav button.active::before {
    inset: 0 14px auto;
    width: auto;
    height: 3px;
    border-radius: 0 0 3px 3px;
  }

  .broker-experience .portal-side__footer {
    position: fixed;
    z-index: 110;
    top: 10px;
    right: 12px;
    display: block;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .broker-experience .portal-content {
    padding: 20px 12px calc(96px + env(safe-area-inset-bottom));
  }

  .broker-experience .table-wrap {
    display: none;
  }

  .broker-mobile-list {
    display: grid;
  }

  .broker-experience .kpi-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
    gap: 9px;
  }

  .broker-experience .kpi-card {
    min-height: 126px;
    padding: 14px;
  }

  .broker-experience .kpi-value {
    font-size: 1.35rem;
  }

  .broker-experience .cms-grid,
  .broker-experience .form-grid,
  .client-experience .form-grid {
    grid-template-columns: 1fr;
  }

  .broker-experience .form-card__body,
  .client-experience .form-card__body {
    padding: 18px;
  }

  .broker-experience .form-card__foot,
  .client-experience .form-card__foot {
    align-items: stretch;
    flex-direction: column;
    padding: 13px 18px;
  }

  .broker-experience .form-card__foot .btn,
  .client-experience .form-card__foot .btn {
    min-height: 46px;
    width: 100%;
  }

  .broker-login-shell,
  .client-login-shell {
    display: flex;
    min-height: 100dvh;
    flex-direction: column;
  }

  .broker-login-panel,
  .client-login-panel {
    order: -1;
    min-height: auto;
    padding: 22px 14px;
    background: #f7f8f9;
  }

  .broker-login-intro,
  .client-login-intro {
    min-height: auto;
    padding: 34px 22px calc(40px + env(safe-area-inset-bottom));
  }

  .client-login-card,
  .broker-login-card {
    width: 100%;
    max-width: 520px;
    padding: 24px 20px;
  }

  .portal-notification-region {
    top: 68px;
    right: 10px;
    width: calc(100vw - 20px);
  }
}

@media (max-width: 480px) {
  .client-experience .client-kpi-grid,
  .broker-experience .kpi-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
  }

  .client-experience .client-kpi-grid .kpi-card,
  .broker-experience .kpi-card {
    min-height: 118px;
  }

  .client-experience .client-side-nav {
    grid-template-columns: repeat(6,minmax(52px,1fr));
  }

  .client-experience .client-side-nav button,
  .broker-experience .side-nav button {
    font-size: .55rem;
  }

  .client-experience .portal-content,
  .broker-experience .portal-content {
    padding-inline: 10px;
  }

  .client-property-hero__meta {
    flex-direction: column;
  }

  .client-property-hero__meta div {
    width: 100%;
  }

  .client-experience .panel,
  .broker-experience .panel,
  .client-experience .form-card,
  .broker-experience .form-card {
    border-radius: 9px;
  }
}


/* Mobile UX system 2026-09 */
:root {
  --mobile-gutter: 16px;
  --mobile-control: 48px;
  --mobile-nav-height: 72px;
  --mobile-radius: 14px;
  --mobile-shadow: 0 8px 28px rgba(9,30,66,.10), 0 1px 2px rgba(9,30,66,.08);
}

body {
  min-width: 320px;
  font-family: "Intro", "Intro Regular", "Avenir Next", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  text-rendering: optimizeLegibility;
}

button,
a,
input,
select,
textarea {
  touch-action: manipulation;
}

#publicShell,
#appShell,
#cmsShell {
  width: 100%;
  overflow-x: clip;
}

.cms-mobile-action {
  display: none;
}

.app-view.active,
.portal-panel.active,
.cms-page.active {
  animation: mobile-surface-enter .22s cubic-bezier(.2,0,0,1);
}

@keyframes mobile-surface-enter {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (hover: hover) and (pointer: fine) {
  .btn:hover,
  .project-card:hover,
  .portal-option:hover {
    transition-duration: .16s;
  }
}

@media (max-width: 760px) {
  html {
    scroll-padding-top: calc(76px + env(safe-area-inset-top));
  }

  body {
    overflow-x: hidden;
    background: var(--surface-sunken);
    font-size: 15px;
  }

  .container {
    width: min(calc(100% - (var(--mobile-gutter) * 2)), var(--max));
  }

  .section {
    padding: 52px 0;
  }

  h1 {
    font-size: clamp(2.1rem, 10vw, 3rem);
    line-height: 1.02;
    letter-spacing: -.045em;
  }

  h2 {
    font-size: clamp(1.65rem, 7vw, 2.15rem);
    letter-spacing: -.035em;
  }

  .lead {
    font-size: 1rem;
    line-height: 1.6;
  }

  .eyebrow {
    margin-bottom: 8px;
    font-size: .68rem;
    line-height: 1.4;
  }

  .btn,
  .mobile-navigation a,
  .mobile-navigation button,
  .slot {
    min-height: 44px;
  }

  .input,
  .select,
  textarea.input {
    min-height: var(--mobile-control);
    border-radius: 10px;
    font-size: 16px;
  }

  textarea.input {
    min-height: 112px;
  }

  input[type="file"].input {
    min-height: auto;
    padding: 11px;
  }

  .field {
    gap: 7px;
    margin-bottom: 16px;
  }

  .field label {
    font-size: .79rem;
    line-height: 1.35;
  }

  .help {
    font-size: .74rem;
    line-height: 1.5;
  }

  .topbar {
    height: calc(62px + env(safe-area-inset-top));
    padding-top: env(safe-area-inset-top);
    background: rgba(255,255,255,.96);
    box-shadow: 0 1px 0 rgba(9,30,66,.08);
  }

  .topbar__inner {
    gap: 10px;
  }

  .brand img {
    width: 38px;
    height: 38px;
  }

  .mobile-menu {
    width: 44px;
    height: 44px;
    border: 1px solid var(--border);
    background: #fff;
  }

  .mobile-navigation {
    max-height: calc(100dvh - 62px - env(safe-area-inset-top));
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom);
    border-radius: 0 0 16px 16px;
    box-shadow: 0 16px 36px rgba(9,30,66,.18);
    -webkit-overflow-scrolling: touch;
  }

  .mobile-navigation .container {
    gap: 4px;
    padding-top: 10px;
    padding-bottom: 14px;
  }

  .mobile-navigation a,
  .mobile-navigation button {
    border-radius: 10px;
    padding-inline: 14px;
    font-size: .9rem;
  }

  .prototype-banner .container {
    min-height: 36px;
    padding-block: 7px;
    text-align: center;
    line-height: 1.35;
  }

  .hero {
    min-height: auto;
    align-items: stretch;
  }

  .hero::before {
    background: linear-gradient(180deg,rgba(0,24,47,.58),rgba(0,24,47,.94) 48%,#001c37 100%);
  }

  .hero__content {
    min-height: calc(100svh - 62px);
    align-content: end;
    gap: 26px;
    padding: 54px 0 30px;
  }

  .hero__brand {
    width: 54px;
    height: 54px;
    margin-bottom: 18px;
  }

  .hero__copy h1 {
    max-width: 12ch;
    margin-bottom: 18px;
    font-size: clamp(2.35rem, 11.7vw, 3.45rem);
    line-height: .98;
  }

  .hero__copy p {
    max-width: 38rem;
    font-size: 1rem;
    line-height: 1.6;
  }

  .hero__actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 9px;
    margin-top: 24px;
  }

  .hero__actions .btn {
    width: 100%;
    min-height: 48px;
  }

  .search-panel {
    width: 100%;
    max-width: none;
    padding: 18px;
    border-color: rgba(255,255,255,.5);
    border-radius: 16px;
    box-shadow: 0 18px 42px rgba(0,15,30,.24);
  }

  .select-row {
    grid-template-columns: 1fr;
  }

  .section-head,
  .cta-card,
  .footer-bottom {
    gap: 18px;
  }

  .section-head {
    margin-bottom: 24px;
  }

  .section-head > div:first-child {
    width: 100%;
  }

  .section-head .btn,
  .cta-card__actions,
  .cta-card__actions .btn {
    width: 100%;
  }

  .project-grid,
  .service-grid,
  .process-grid {
    gap: 14px;
  }

  .project-card,
  .service-card,
  .process-card,
  .cta-card {
    border-radius: var(--mobile-radius);
    box-shadow: var(--shadow-raised);
  }

  .project-card__media {
    aspect-ratio: 4 / 2.55;
  }

  .project-card__body {
    padding: 18px;
  }

  .project-card__foot {
    align-items: stretch;
    flex-direction: column;
  }

  .project-card__foot .text-link {
    min-height: 40px;
    justify-content: flex-start;
    padding-inline: 0;
  }

  .cta-card {
    padding: 24px 20px;
  }

  .footer {
    padding: 52px 0 calc(28px + env(safe-area-inset-bottom));
  }

  .footer-grid {
    gap: 28px;
  }

  .legal-links {
    gap: 10px 16px;
    line-height: 1.6;
  }

  .app-topbar {
    min-height: calc(62px + env(safe-area-inset-top));
    padding-top: env(safe-area-inset-top);
    background: rgba(255,255,255,.96);
    box-shadow: 0 1px 0 rgba(9,30,66,.08);
  }

  .app-topbar__inner {
    min-height: 62px;
    gap: 9px;
  }

  .app-topbar__inner > .btn:first-child {
    width: 42px;
    min-width: 42px;
    padding: 0;
    border: 1px solid var(--border);
    font-size: 0;
  }

  .app-topbar__inner > .btn:first-child svg {
    width: 19px;
    height: 19px;
  }

  .app-topbar__inner > img {
    width: 32px !important;
    height: 32px !important;
  }

  .app-topbar__title {
    flex: 1;
    min-width: 0;
  }

  .app-topbar__title strong {
    overflow: hidden;
    font-size: .8rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .app-topbar__actions {
    display: none;
  }

  .app-page {
    padding: 24px 0 56px;
  }

  .page-heading,
  .case-hero,
  .cms-heading {
    gap: 14px;
    margin-bottom: 20px;
  }

  .page-heading h1,
  .portal-panel .page-heading h1 {
    margin-bottom: 7px;
    font-size: clamp(1.72rem, 7.8vw, 2.15rem);
    line-height: 1.08;
  }

  .page-heading p,
  .cms-heading p {
    font-size: .9rem;
    line-height: 1.55;
  }

  .page-heading > .lozenge {
    align-self: flex-start;
  }

  .panel,
  .form-card,
  .summary-card,
  .detail-card,
  .unit-card,
  .print-card {
    border-radius: var(--mobile-radius);
    box-shadow: 0 1px 2px rgba(9,30,66,.06);
  }

  .panel[style*="padding:18px"] {
    padding: 16px !important;
  }

  .panel__head {
    min-height: 0;
    padding: 16px;
  }

  .panel__head h3 {
    margin-bottom: 3px;
  }

  .form-layout,
  .payment-grid,
  .editor-grid,
  .cms-grid,
  .detail-grid {
    grid-template-columns: minmax(0,1fr);
    gap: 14px;
  }

  .form-card {
    overflow: clip;
  }

  .form-card__head {
    align-items: flex-start;
    padding: 16px;
  }

  .form-card__head svg {
    flex: 0 0 auto;
  }

  .form-card__body {
    padding: 18px 16px;
  }

  .form-card__foot {
    align-items: stretch;
    flex-direction: column-reverse;
    gap: 8px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }

  .form-card__foot .btn {
    width: 100%;
    min-height: 48px;
  }

  .form-grid {
    grid-template-columns: minmax(0,1fr);
    gap: 15px;
  }

  .field--full {
    grid-column: auto;
  }

  .stepper {
    display: flex;
    gap: 8px;
    margin: -2px 0 16px;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
  }

  .stepper::-webkit-scrollbar {
    display: none;
  }

  .stepper__item {
    min-width: 126px;
    min-height: 42px;
    display: grid;
    align-items: center;
    flex: 1 0 126px;
    padding: 10px 12px;
    scroll-snap-align: start;
  }

  .summary-card {
    position: static;
    order: -1;
    padding: 18px;
  }

  .summary-row {
    gap: 18px;
    font-size: .8rem;
  }

  .summary-row strong {
    max-width: 62%;
    overflow-wrap: anywhere;
  }

  .notice {
    padding: 14px;
    border-radius: 10px;
    font-size: .8rem;
    line-height: 1.5;
  }

  .inventory-grid {
    grid-template-columns: minmax(0,1fr);
    gap: 14px;
  }

  .unit-card img {
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .unit-card__body {
    padding: 18px;
  }

  .case-hero {
    padding: 22px 18px;
    border-radius: 16px;
  }

  .case-hero h1 {
    font-size: 2rem;
  }

  .slot-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
    gap: 9px;
  }

  .slot {
    min-height: 46px;
  }

  .print-card {
    padding: 20px 18px;
  }

  .login-wrap,
  .client-login-shell,
  .broker-login-shell {
    min-height: calc(100dvh - 62px - env(safe-area-inset-top));
  }

  .login-panel,
  .client-login-panel,
  .broker-login-panel {
    align-content: start;
    padding: 20px var(--mobile-gutter) 26px;
  }

  .login-card,
  .client-login-card,
  .broker-login-card {
    width: 100%;
    max-width: 540px;
    padding: 22px 18px;
    border-radius: 16px;
    box-shadow: var(--mobile-shadow);
  }

  .login-card__logo {
    width: 52px;
    height: 52px;
    margin-bottom: 15px;
  }

  .login-card h1,
  .client-login-card h1,
  .broker-login-card h1 {
    font-size: 1.65rem;
  }

  .client-login-intro,
  .broker-login-intro {
    padding: 32px 20px calc(36px + env(safe-area-inset-bottom));
  }

  .client-mobile-preview {
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;
  }

  .client-mobile-preview::-webkit-scrollbar {
    display: none;
  }

  .client-mobile-preview > * {
    min-width: 132px;
  }

  .broker-register-page {
    padding: 20px var(--mobile-gutter) 48px;
  }

  .broker-register-summary {
    padding: 18px;
    border-radius: var(--mobile-radius);
  }

  body.client-portal-mode .app-topbar,
  body.broker-portal-mode .app-topbar,
  body.client-portal-mode .app-topbar__inner,
  body.broker-portal-mode .app-topbar__inner {
    min-height: calc(62px + env(safe-area-inset-top));
  }

  body.client-portal-mode .app-topbar__inner,
  body.broker-portal-mode .app-topbar__inner {
    padding-inline: var(--mobile-gutter) 58px;
  }

  body.client-portal-mode .app-topbar__title strong,
  body.broker-portal-mode .app-topbar__title strong {
    max-width: min(42vw,190px);
  }

  body.client-portal-mode .app-legal-footer,
  body.broker-portal-mode .app-legal-footer {
    display: none;
  }

  .client-experience .portal-layout,
  .broker-experience .portal-layout {
    min-height: calc(100dvh - 62px - env(safe-area-inset-top));
  }

  .client-experience .portal-side,
  .broker-experience .portal-side {
    height: calc(var(--mobile-nav-height) + env(safe-area-inset-bottom));
    padding: 6px 6px calc(6px + env(safe-area-inset-bottom));
    border-top-color: rgba(9,30,66,.12);
    background: rgba(255,255,255,.94);
    box-shadow: 0 -8px 24px rgba(9,30,66,.10);
    backdrop-filter: saturate(150%) blur(18px);
  }

  .client-experience .client-side-nav,
  .broker-experience .side-nav {
    height: 60px;
    align-items: stretch;
    grid-auto-flow: column;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
  }

  .client-experience .client-side-nav {
    grid-template-columns: repeat(6,minmax(60px,1fr));
  }

  .broker-experience .side-nav {
    grid-template-columns: repeat(5,minmax(64px,1fr));
  }

  .client-experience .client-side-nav button,
  .broker-experience .side-nav button {
    min-width: 58px;
    min-height: 58px;
    gap: 3px;
    padding: 5px 3px;
    border-radius: 10px;
    scroll-snap-align: center;
    font-size: .58rem;
    line-height: 1.1;
  }

  .client-experience .client-side-nav button.active,
  .broker-experience .side-nav button.active {
    background: var(--acc-navy-soft);
    color: var(--acc-navy);
  }

  .client-experience .portal-side__footer,
  .broker-experience .portal-side__footer {
    top: calc(10px + env(safe-area-inset-top));
    right: 10px;
  }

  .client-experience .portal-logout,
  .broker-experience .portal-logout {
    width: 42px;
    min-width: 42px;
    min-height: 42px;
    padding: 0;
    border: 1px solid #ffbdad;
    border-radius: 10px;
    font-size: 0;
  }

  .client-experience .portal-content,
  .broker-experience .portal-content {
    padding: 22px var(--mobile-gutter) calc(104px + env(safe-area-inset-bottom));
  }

  .client-experience .page-heading,
  .broker-experience .page-heading {
    gap: 12px;
    margin-bottom: 18px;
  }

  .client-experience .page-heading > .btn,
  .broker-experience .page-heading > .btn,
  .client-experience .portal-actions,
  .broker-experience .portal-actions {
    width: 100%;
  }

  .client-experience .page-heading > .btn,
  .broker-experience .page-heading > .btn,
  .client-experience .portal-actions .btn,
  .broker-experience .portal-actions .btn {
    min-height: 46px;
  }

  .client-property-hero {
    gap: 18px;
    padding: 19px 17px;
    border-radius: 16px;
  }

  .client-property-hero__copy {
    min-width: 0;
  }

  .client-property-hero__copy h2,
  .client-property-hero__copy span,
  .client-property-hero__meta strong {
    overflow-wrap: anywhere;
  }

  .client-kpi-grid,
  .broker-experience .kpi-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
    gap: 10px;
  }

  .client-kpi-grid .kpi-card,
  .broker-experience .kpi-card {
    min-width: 0;
    min-height: 122px;
    padding: 14px;
    border-radius: 13px;
  }

  .client-kpi-grid .kpi-value,
  .broker-experience .kpi-value {
    font-size: clamp(1.15rem,5.5vw,1.4rem);
    overflow-wrap: anywhere;
  }

  .client-mobile-card,
  .broker-mobile-card {
    padding: 16px;
  }

  .client-mobile-card__top,
  .client-mobile-card__row,
  .broker-mobile-card__top,
  .broker-mobile-card__row {
    align-items: flex-start;
  }

  .client-mobile-card__row strong,
  .broker-mobile-card__row strong {
    max-width: 60%;
    overflow-wrap: anywhere;
  }

  .client-submission-list,
  .client-appointment-list,
  .activity-list,
  .timeline {
    min-width: 0;
  }

  .portal-notification-region {
    top: calc(70px + env(safe-area-inset-top));
    right: 10px;
    width: calc(100vw - 20px);
  }

  .portal-notification {
    width: 100%;
    border-radius: 12px;
    box-shadow: var(--mobile-shadow);
  }

  .app-legal-footer {
    padding: 20px 0 calc(20px + env(safe-area-inset-bottom));
  }

  .app-legal-footer .container {
    align-items: flex-start;
    gap: 14px;
  }

  dialog,
  .document-dialog {
    width: 100vw;
    max-width: none;
    max-height: min(92dvh,900px);
    margin: auto 0 0;
    border-radius: 20px 20px 0 0;
    overflow: auto;
    overscroll-behavior: contain;
  }

  .dialog-head {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 14px 16px;
    background: rgba(255,255,255,.96);
    backdrop-filter: blur(16px);
  }

  .dialog-body {
    padding: 18px 16px;
  }

  .dialog-actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: rgba(255,255,255,.96);
    backdrop-filter: blur(16px);
  }

  .dialog-actions .btn {
    width: 100%;
    min-height: 48px;
  }

  .project-dialog__image {
    aspect-ratio: 16 / 10;
  }

  .project-gallery__stage {
    border-radius: 12px;
  }

  .project-gallery__thumbs {
    grid-auto-columns: 82px;
    gap: 8px;
  }

  .cookie-banner {
    left: 10px;
    right: 10px;
    bottom: calc(10px + env(safe-area-inset-bottom));
    max-height: calc(100dvh - 20px);
    overflow-y: auto;
    gap: 16px;
    padding: 18px;
    border-radius: 16px;
    box-shadow: 0 18px 52px rgba(9,30,66,.24);
  }

  body.client-portal-mode .cookie-banner,
  body.broker-portal-mode .cookie-banner,
  body.cms-mode .cookie-banner {
    bottom: calc(var(--mobile-nav-height) + 10px + env(safe-area-inset-bottom));
  }

  .cookie-banner__actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .cookie-banner__actions .btn {
    width: 100%;
  }

  .toast {
    bottom: calc(14px + env(safe-area-inset-bottom));
    width: calc(100vw - 24px);
    max-width: 520px;
    padding: 14px 16px;
    border-radius: 12px;
  }

  body.client-portal-mode .toast,
  body.broker-portal-mode .toast,
  body.cms-mode .toast {
    bottom: calc(var(--mobile-nav-height) + 12px + env(safe-area-inset-bottom));
  }

  body.cms-mode #cmsShell {
    display: block;
    min-height: 100dvh;
    padding-bottom: calc(var(--mobile-nav-height) + env(safe-area-inset-bottom));
  }

  .cms-main {
    min-width: 0;
  }

  .cms-topbar {
    position: sticky;
    top: 0;
    z-index: 70;
    min-height: calc(60px + env(safe-area-inset-top));
    padding: env(safe-area-inset-top) 12px 0;
    gap: 8px;
    background: rgba(255,255,255,.96);
    backdrop-filter: blur(16px);
  }

  .cms-topbar h1 {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-size: .9rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cms-topbar__actions {
    display: flex;
    gap: 4px;
  }

  .cms-topbar__actions > .btn:first-child {
    width: 40px;
    min-width: 40px;
    padding: 0;
  }

  .cms-mobile-action {
    display: inline-flex;
    width: 40px;
    min-width: 40px;
    padding: 0;
  }

  .cms-sidebar {
    height: calc(var(--mobile-nav-height) + env(safe-area-inset-bottom));
    padding: 5px 6px calc(5px + env(safe-area-inset-bottom));
    background: rgba(0,36,70,.97);
    box-shadow: 0 -8px 24px rgba(9,30,66,.18);
    backdrop-filter: saturate(150%) blur(18px);
  }

  .cms-nav {
    display: grid;
    grid-template-columns: repeat(4,minmax(0,1fr));
    gap: 3px;
  }

  .cms-nav button {
    width: auto;
    min-width: 0;
    min-height: 60px;
    flex-direction: column;
    gap: 3px;
    justify-content: center;
    padding: 5px 2px;
    border-radius: 10px;
    font-size: .58rem;
    line-height: 1.1;
  }

  .cms-nav button span {
    display: block;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cms-nav button svg {
    width: 19px;
    height: 19px;
  }

  .cms-content {
    padding: 22px var(--mobile-gutter) calc(28px + env(safe-area-inset-bottom));
  }

  .cms-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .cms-heading h2 {
    margin-bottom: 8px;
    font-size: 1.75rem;
  }

  .cms-heading > .btn,
  .cms-heading > div:last-child:not(:first-child) {
    width: 100%;
  }

  .cms-heading > div:last-child:not(:first-child) {
    display: grid !important;
    grid-template-columns: 1fr;
  }

  .cms-heading > div:last-child .btn {
    width: 100%;
    min-height: 46px;
  }

  .cms-content .kpi-grid {
    grid-template-columns: repeat(2,minmax(0,1fr));
    gap: 10px;
  }

  .cms-content .kpi-card {
    min-width: 0;
    min-height: 126px;
    padding: 14px;
    border-radius: 13px;
  }

  .cms-content .kpi-value {
    font-size: 1.35rem;
    overflow-wrap: anywhere;
  }

  .toolbar {
    display: grid;
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .toolbar .input,
  .toolbar .select {
    width: 100%;
    min-width: 0;
  }

  .table-wrap {
    max-width: 100%;
    overflow: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }

  .cms-content .table-wrap table {
    min-width: 680px;
  }

  .cms-access-backdrop {
    align-items: end;
    padding: 0;
  }

  .cms-access-dialog {
    width: 100%;
    max-width: none;
    max-height: 92dvh;
    overflow-y: auto;
    padding: 28px 20px calc(22px + env(safe-area-inset-bottom));
    border-radius: 20px 20px 0 0;
  }

  #printContent {
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .print-sheet {
    min-width: 0;
    padding: 20px 16px;
  }
}

@media (max-width: 480px) {
  :root {
    --mobile-gutter: 12px;
  }

  .section {
    padding: 44px 0;
  }

  .hero__content {
    padding-top: 42px;
  }

  .search-panel,
  .login-card,
  .client-login-card,
  .broker-login-card {
    padding: 20px 16px;
  }

  .client-experience .portal-content,
  .broker-experience .portal-content,
  .cms-content {
    padding-inline: var(--mobile-gutter);
  }

  .client-property-hero__meta {
    gap: 0;
  }

  .client-property-hero__meta div + div {
    margin-top: 10px;
  }

  .client-mobile-card,
  .broker-mobile-card {
    padding: 14px;
  }

  .print-sheet {
    padding: 22px;
  }
}

@media (max-width: 360px) {
  .client-kpi-grid,
  .broker-experience .kpi-grid,
  .cms-content .kpi-grid {
    grid-template-columns: minmax(0,1fr);
  }

  .app-topbar__inner > img {
    display: none !important;
  }

  body.client-portal-mode .app-topbar__title strong,
  body.broker-portal-mode .app-topbar__title strong {
    max-width: 40vw;
  }
}

@media (max-width: 900px) and (max-height: 500px) and (orientation: landscape) {
  .client-login-intro,
  .broker-login-intro {
    display: none;
  }

  .login-panel,
  .client-login-panel,
  .broker-login-panel {
    min-height: calc(100dvh - 62px);
    align-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }

  .app-view.active,
  .portal-panel.active,
  .cms-page.active {
    animation: none;
  }
}


/* Routed navigation */
.route-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  margin: 0 0 18px;
  color: var(--text-subtlest);
  font-size: .78rem;
  line-height: 1.4;
  white-space: nowrap;
}
.route-breadcrumbs a {
  color: var(--text-subtle);
  font-weight: 700;
  text-decoration: none;
}
.route-breadcrumbs a:hover {
  color: var(--acc-navy);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.route-breadcrumbs [aria-current="page"] {
  min-width: 0;
  overflow: hidden;
  color: var(--text);
  font-weight: 750;
  text-overflow: ellipsis;
}
.route-breadcrumbs__separator {
  flex: 0 0 auto;
  color: var(--border-bold);
}

.availability-more {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px 20px;
  border-top: 1px solid var(--border);
}
.availability-more__link {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--info-text);
  font-size: .88rem;
  font-weight: 800;
  text-decoration: none;
}
.availability-more__link:hover {
  background: var(--info-bg);
}

@media (max-width: 700px) {
  .route-breadcrumbs {
    width: 100%;
    margin-bottom: 14px;
    overflow-x: auto;
    padding-bottom: 3px;
    scrollbar-width: none;
  }
  .route-breadcrumbs::-webkit-scrollbar {
    display: none;
  }
  .availability-more {
    justify-content: stretch;
    padding: 12px 16px 16px;
  }
  .availability-more__link {
    width: 100%;
    min-height: 46px;
    justify-content: space-between;
    padding-inline: 12px;
  }
}
